import {listingRow,orderProductInput} from '../features/erp/orderListing';
import {validateGroupText} from '../features/erp/groupText';
import {liveProductFilters} from '../features/erp/liveSellableQuantity';
import {productSortRates} from '../features/erp/orderSort';
import {readListSettings} from '../hooks/localListSettings';
import {directMediaForm,uploadMedia,MEDIA_UPLOAD_CONCURRENCY} from './mediaUpload';
import {preparePrivateImage} from '../utils/imageMetadata';
import type { ImageVariants } from '../types/image';
import { teamFetch } from './teamTransport';
import type { ProductPricingConfig as ApiPricingRuleConfig, ProductPricing, ProductPricingInput, ProductPurchaseBudget } from '../types/productPricing';
import { commitStreamChange } from './dataSync';
import { peekQuery, putQuery, readQuery } from './queryCache';
import { ECOMMERCE_API_BASE_URL } from '../config';
import { apiErrorMessage, NETWORK_ERROR_MESSAGE, readErrorBody } from './errorMessage';
import type { SupplierProduct } from './supplier';
import { getSupplierToken } from '../features/supplier/supplierAccess';

export type SupplierCollaboration = {id:number;suppliers:Array<{id:number;name:string|null}>};

const BASE = `${ECOMMERCE_API_BASE_URL}/erp`;
const PRODUCT_BASE = `${ECOMMERCE_API_BASE_URL}/product`;

export interface ApiCalendarSession {
    session_id?:number|null; target_kind?:'session'|'purge'; warehouse_id?:number|null;
    id: number; livestream_id: number; scheduled_at: string | null;
    series_name: string; series_title?: string | null; live_type: string; series_status: LivestreamStatus; series_type?: 'live' | 'purge';
    streamer_id: number | null; streamer_name: string | null;
    director_id: number | null; director_name: string | null;
    returned_at: string | null;
    returned_import_id?: number | null;
    cover_ready?: boolean;
    other_preparation_ready?: boolean;
}
export interface ApiHomeStock {
    warehouses: Array<{ id: number; name: string; inventory_skus: number; inventory_quantity: number }>;
    inventory_skus: number; inventory_quantity: number;
    catalog_skus: number; catalog_suppliers: number;
    catalog_modes: Array<{ maintenance_mode: 'supplier' | 'withpot'; catalog_skus: number; catalog_suppliers: number }>;
}
export const getHomeStock = (signal?: AbortSignal) => request<ApiHomeStock>('/home/stock', homeReadOptions(signal));
export interface ApiMonthlySales {
    month: string; quantity: number; revenue: number; currency: string; timezone: string;
    previous_quantity: number; previous_revenue: number;
    revenue_change_percent?: number | null; quantity_change_percent?: number | null;
}
const homeReadOptions = (signal?: AbortSignal): RequestInit => ({ signal, headers: { Authorization: `Bearer ${getSupplierToken() ?? ''}` } });
export const getHomeCalendar = (month: string, timezone: string, signal?: AbortSignal, seriesType?: 'live' | 'purge') =>
    request<{ items: ApiCalendarSession[] }>(`/home/calendar?${new URLSearchParams({ month, timezone, ...(seriesType ? { series_type: seriesType } : {}) })}`, homeReadOptions(signal));
export interface ApiSeriesCalendarEntry {
    id: number; name: string | null; title: string | null; status: LivestreamStatus;
    scheduled_at: string; warehouse_name: string | null;
    session_count: number; returned_count: number;
}
export const getHomeSeriesCalendar = (month: string, timezone: string, signal?: AbortSignal) =>
    request<{items: ApiSeriesCalendarEntry[]}>(`/home/series-calendar?${new URLSearchParams({month, timezone})}`, homeReadOptions(signal));
export interface ApiPurchaseCalendarEntry {
    source_series_type?:'live'|'purge'|null; source_title?:string|null;
    id: number; name: string | null; order_code: string;
    expected_arrival_date: string; status: 'selection' | 'in_progress' | 'completed'; status_label?:string;
    total_received_quantity: number; total_line_target_quantity: number;
    warehouse_name: string | null; warehouse_code: string | null;
}
export const getHomePurchaseCalendar = (month: string, signal?: AbortSignal) =>
    request<{items: ApiPurchaseCalendarEntry[]}>(`/home/purchase-calendar?${new URLSearchParams({month})}`, homeReadOptions(signal));
export const getHomeSales = (month: string, signal?: AbortSignal) => request<ApiMonthlySales>(`/home/sales?month=${month}`, homeReadOptions(signal));

export class ApiRequestError extends Error {
    status: number;
    body: unknown;
    data: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = 'ApiRequestError';
        this.status = status;
        this.body = body;
        this.data = body && typeof body === 'object' && 'data' in body
            ? (body as { data?: unknown }).data
            : body;
        Object.setPrototypeOf(this, ApiRequestError.prototype);
    }
}

const responseErrorBody = readErrorBody;

function responseErrorMessage(res: Response, body: unknown): string {
    const data = body as any;
    const detail = data && typeof data === 'object' && data.error && typeof data.error === 'object' ? data.error : null;
    if (!detail || Array.isArray(detail)) return apiErrorMessage(res.status, body);
    const message = detail.code === 'missing_product_measurements'
        ? '请先补全花盆尺寸和重量'
        : apiErrorMessage(res.status, { error: detail.message, request_id: data.request_id });
    const missingFields = Array.isArray(detail.missing_fields)
        ? detail.missing_fields
            .map((field: unknown) => RECEIVE_FIELD_LABELS[String(field)])
            .filter(Boolean)
        : [];
    return missingFields.length ? `${message}（缺少：${missingFields.join('、')}）` : message;
}

async function apiError(res: Response): Promise<ApiRequestError> {
    const body = await responseErrorBody(res);
    return new ApiRequestError(responseErrorMessage(res, body), res.status, body);
}

const RECEIVE_FIELD_LABELS: Record<string, string> = {
    height: '高度',
    width: '宽度',
    depth: '长度',
    weight: '重量',
};

function erpFetch(url: string, options?: RequestInit): Promise<Response> {
    return teamFetch(url, options, () => new ApiRequestError(NETWORK_ERROR_MESSAGE, 0, null));
}

async function request<T>(path: string, opts?: RequestInit, fresh = false): Promise<T> {
    const load = async (signal?: AbortSignal) => {
        const parent=signal??opts?.signal;
        const printing=/^\/(?:product-print-labels|print-label-images|print-task-(?:sources|products)|packaging\/scheme)(?:[/?]|$)/.test(path)
            || /^\/livestreams\/\d+\/export(?:[/?]|$)/.test(path);
        const controller=printing?new AbortController():undefined;
        const cancel=()=>controller?.abort();
        if(parent?.aborted)cancel();else parent?.addEventListener('abort',cancel,{once:true});
        const timer=controller?setTimeout(cancel,path.startsWith('/print-label-images')?45000:15000):undefined;
        try{
            const res = await erpFetch(`${BASE}${path}`, {
                ...opts, signal: controller?.signal ?? parent,
                headers: { ...(opts?.body ? { 'Content-Type': 'application/json' } : {}), ...opts?.headers },
            });
            if (!res.ok) {
                const body = await responseErrorBody(res);
                throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
            }
            return await res.json() as T;
        }catch(error){
            if(controller?.signal.aborted&&!parent?.aborted)throw new Error('打印请求超时，请重试');
            throw error;
        }finally{if(timer!==undefined)clearTimeout(timer);parent?.removeEventListener('abort',cancel);}
    };
    if ((opts?.method ?? 'GET').toUpperCase() !== 'GET') return load();
    return readQuery(`raw|${BASE}${path}`, load, {
        signal: opts?.signal ?? undefined,
        staleTime: fresh ? 0 : /^\/(livestreams|procurements|inventory|customers|returned-orders|streamers|suppliers|procurement-suppliers|product-categories|warehouses|procurement-warehouses|livestream-types|product-attributes|live-columns|settings-snapshot)(?:\/|\?|$)/.test(path) ? 30_000 : 3_000,
    });
}

async function productJsonRequest<T>(path: string, opts?: RequestInit): Promise<T> {
    const res = await erpFetch(`${PRODUCT_BASE}${path}`, {
        headers: { ...(opts?.body ? { 'Content-Type': 'application/json' } : {}), ...opts?.headers },
        ...opts,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

async function productFormRequest<T>(path: string, form: FormData): Promise<T> {
    const res = await erpFetch(`${PRODUCT_BASE}${path}`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

async function erpFormRequest<T>(path: string, form: FormData, headers?:Record<string,string>,signal?:AbortSignal): Promise<T> {
    const res = await erpFetch(`${BASE}${path}`, {
        method: 'POST',
        body: form,
        headers,signal,
    });
    if (!res.ok) {
        const body = await responseErrorBody(res);
        throw new ApiRequestError(responseErrorMessage(res, body), res.status, body);
    }
    return res.json();
}

// ─── Streamers ────────────────────────────────────────────────────────────────

export interface ApiStreamer {
    english_name?:string|null;
    can_delete?:boolean; delete_disabled_reason?:string|null;
    employment_type?:'full_time'|'part_time';
    roles?:string[];
    identity_number?:string|null;
    employment_start?:string|null; employment_end?:string|null;
    payment?:MemberPayment;
    /** Authoritative salary in CNY; USD is a display conversion. */
    base_salary?:string|null;
    commission_rate?:string|null;
    streamer_commission_rate?:string|null;
    director_commission_rate?:string|null;
    id: number;
    name: string;
    note: string;
    /** Personal contact only. */
    phone: string | null;
    /** Optional personal email; supplied addresses must have a valid format. */
    email: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getStreamers = (includeInactive = false, signal?: AbortSignal) =>
    request<ApiStreamer[]>(`/streamers${includeInactive ? '?include_inactive=true' : ''}`, {signal});
export type MemberPayment = {recipient?:string|null;account?:string|null;bank?:string|null;branch?:string|null;branch_code?:string|null};
export interface MemberChanges {
    english_name?:string|null;
    employment_type?:'full_time'|'part_time';
    name?:string; email?:string; phone?:string; roles?:string[]; identity_number?:string;
    employment_start?:string|null; employment_end?:string|null;
    payment?:MemberPayment; base_salary?:string|null; commission_rate?:string|null;
    streamer_commission_rate?:string|null; director_commission_rate?:string|null;
}
export const verifySettingsAccess = (section:string,password:string,signal?:AbortSignal) =>
    request<{verified:boolean}>('/settings-access/verify', {method:'POST',body:JSON.stringify({section,password}),signal});
export const createStreamer = (payload: MemberChanges & {name:string;email:string}) =>
    request<ApiStreamer>('/streamers', { method: 'POST', body: JSON.stringify(payload) });
export const updateStreamer = (id: number, payload: MemberChanges) =>
    request<Partial<ApiStreamer> & {id: number}>(`/streamers/${id}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteStreamer = (id: number) =>
    request<{ message: string }>(`/streamers/${id}`, { method: 'DELETE' });

// ─── Suppliers ────────────────────────────────────────────────────────────────

export interface ApiSupplier {
    id: number;
    name: string;
    /** 联系方式。改它不影响任何人登录。 */
    contact_phone: string | null;
    /** 登录账号（登录 = 手机号 + 密码），由团队在货盘里设置。这里只读。 */
    wechat: string | null;
    brand_story: string | null;
    /** 产地：供应商在 App 的「我的 → 地点」里填的。ERP 读这一份。 */
    location: { country: string | null; city: string | null; display_name: string | null } | null;
    /** App 合作 / 线下 / 网络采集；null = 未分类。 */
    maintenance_mode: 'supplier' | 'withpot';
    login_account: string;
    online_channel_type?: string | null;
    online_channel_url?: string | null;
    /** 旧的产地字典。前端已不再维护，保留字段供过渡期人工清理和迁移。 */
    is_active: boolean;
    created_at: string;
    updated_at: string;
}



export const getSuppliers = (signal?: AbortSignal) => request<ApiSupplier[]>('/procurement-suppliers', {signal});
type SupplierKindPayload = { maintenance_mode?: 'supplier' | 'withpot'; online_channel_type?: string | null; online_channel_url?: string | null };
export const deleteSupplier = (id: number) =>
    request<ApiSupplier>(`/procurement-suppliers/${id}`, { method: 'DELETE' });

export type { ProductPricingConfig as ApiPricingRuleConfig } from '../types/productPricing';

export interface ApiPricingRuleVersion {
    id: number;
    version: string;
    config: ApiPricingRuleConfig;
}

/** Pure preview through the shared server calculator; never saves the product or changes rules. */
export const calculateProductPricing = (product: ProductPricingInput, signal?: AbortSignal) =>
    request<ProductPricing>('/pricing/calculate', { method: 'POST', body: JSON.stringify({ product }), signal });

export const getPricingRules = (signal?:AbortSignal) => request<{
    active: ApiPricingRuleVersion | null;
    defaults: ApiPricingRuleConfig;
    premium_targets?: {brands:{id:number;name:string}[];series:{id:number;brand_id:number;name:string}[]};
}>('/pricing-rules', {signal});
export const savePricingRuleChanges = (payload:{base_version:string|null;changes:Record<string,unknown>}) =>
    request<{id:number;version:string;changes:Partial<ApiPricingRuleConfig>}>('/pricing-rules', {method:'PATCH',body:JSON.stringify(payload)});
// ─── Product Categories ───────────────────────────────────────────────────────

export interface ApiProductCategory {
    id: number;
    name: string;
    prompt_profile?: string;
    white_bg_prompt: string | null;
    lifestyle_prompt: string | null;
    notes: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getProductCategories = (signal?: AbortSignal) => request<ApiProductCategory[]>('/product-categories', {signal});
export const createProductCategory = (payload: { name: string; prompt_profile?:string; white_bg_prompt: string; lifestyle_prompt: string; notes?: string }) =>
    request<ApiProductCategory>('/product-categories', { method: 'POST', body: JSON.stringify(payload) });
export const updateProductCategory = (id: number, payload: { name?: string; prompt_profile?:string; white_bg_prompt?: string; lifestyle_prompt?: string; notes?: string }) =>
    request<Partial<ApiProductCategory> & {id: number}>(`/product-categories/${id}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProductCategory = (id: number) =>
    request<ApiProductCategory>(`/product-categories/${id}`, { method: 'DELETE' });

// ─── Warehouses ───────────────────────────────────────────────────────────────

export interface ApiWarehouse {
    can_delete?:boolean; delete_disabled_reason?:string|null;
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    sort_order: number;

    inventory_location_id: number | null;
    inventory_location_code: string | null;
    inventory_location_name: string | null;
    /** Where suppliers ship to. Copied onto an order when it is pushed. */
    address: string | null;
    door_number?: string | null;
    /** Legacy association is read only; new contacts belong to the warehouse. */
    recipient_streamer_id: number | null;
    recipient_phone: string | null;
    created_at: string;
    updated_at: string;
}

export const getWarehouses = (includeInactive = false, signal?: AbortSignal) =>
    request<ApiWarehouse[]>(`/procurement-warehouses${includeInactive ? '?include_inactive=true' : ''}`, {signal});
export const createWarehouse = (payload: { name: string; code?: string; inventory_location_id?: number; address: string; door_number?: string; recipient_phone: string }) =>
    request<ApiWarehouse>('/procurement-warehouses', { method: 'POST', body: JSON.stringify(payload) });
export const updateWarehouse = (id: number, payload: Partial<{ name: string; is_active: boolean; inventory_location_id: number; address: string; door_number?: string; recipient_phone: string }>) =>
    request<Partial<ApiWarehouse> & {id: number}>(`/procurement-warehouses/${id}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteWarehouse = (id: number) =>
    request<{ message: string }>(`/procurement-warehouses/${id}`, { method: 'DELETE' });

// ─── Live Types (read-only) ───────────────────────────────────────────────────

export interface ApiLiveType {
    id: string;
    value: string;
    name: string;
    label: string;
    location_code: string;
    is_active: boolean;
}

export const getLiveTypes = (signal?: AbortSignal) => request<ApiLiveType[]>('/livestream-types', {signal});

// ─── Product Attributes ───────────────────────────────────────────────────────

export interface ApiAttributeOption {
    id: number;
    attribute_id: number;
    name: string;
    is_active: boolean;
    sort_order: number;
}

export interface ApiProductAttribute {
    id: number;
    key: string;
    name: string;
    note: string;
    is_required: boolean;
    selection_mode: 'single' | 'multiple';
    default_option_id: number | null;
    visibility_rule: { attribute_id: number; option_ids: number[] } | null;
    is_active: boolean;
    sort_order: number;
    option_count: number;
    options: ApiAttributeOption[];
    created_at: string;
    updated_at: string;
}

export const getProductAttributes = (includeInactive = false, signal?: AbortSignal) =>
    request<ApiProductAttribute[]>(`/product-attributes?include_options=true${includeInactive ? '&include_inactive=true' : ''}`, {signal});

/** The six active settings lists in one read; each equals its own endpoint's default read. */
export interface ApiLiveColumn {column_type:'live'|'purge';can_delete?:boolean;delete_disabled_reason?:string|null;id:number;key:string;name:string;sort_order:number;revision:number;masthead_options?:{value:string;label:string}[];style:string;masthead:string;direction:string;text_color:'black'|'white'|'auto';sticker_prompt:string;size_config:Record<string,unknown>;group_preset:string;}
export const deleteLiveColumn = (id:number) => request<{deleted:boolean;id:number}>(`/live-columns/${id}`,{method:'DELETE'});
export const getLiveColumns = (signal?:AbortSignal) => request<ApiLiveColumn[]>('/live-columns',{signal});
export const updateLiveColumn = (id:number,patch:Partial<ApiLiveColumn>) => request<Partial<ApiLiveColumn>&{id:number;revision:number}>(`/live-columns/${id}`,{method:'PATCH',body:JSON.stringify(patch)});
export interface ApiSettingsSnapshotData {
    live_columns?: ApiLiveColumn[];
    streamers: ApiStreamer[];
    suppliers: ApiSupplier[];
    product_categories: ApiProductCategory[];
    warehouses: ApiWarehouse[];
    livestream_types: ApiLiveType[];
    product_attributes: ApiProductAttribute[];
}
export type ApiSettingsSnapshot =
    | { version: string; unchanged: true }
    | { version: string; unchanged: false; data: ApiSettingsSnapshotData };
export const getSettingsSnapshot = (known?: string) =>
    request<ApiSettingsSnapshot>(`/settings-snapshot${known ? `?known=${encodeURIComponent(known)}` : ''}`);
export const createProductAttribute = (payload: { name: string; note?: string; key?: string; is_required?: boolean; selection_mode?: 'single' | 'multiple' }) =>
    request<ApiProductAttribute>('/product-attributes', { method: 'POST', body: JSON.stringify(payload) });
export const updateProductAttribute = (id: number, payload: Partial<{ name: string; note: string; is_active: boolean; is_required: boolean; selection_mode: 'single' | 'multiple'; default_option_id: number | null; visibility_rule: { attribute_id: number; option_ids: number[] } | null }>) =>
    request<Partial<ApiProductAttribute> & {id: number}>(`/product-attributes/${id}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProductAttribute = (id: number) =>
    request<{ message: string }>(`/product-attributes/${id}`, { method: 'DELETE' });



export const createAttributeOption = (attrId: number, payload: { name: string }) =>
    request<ApiAttributeOption>(`/product-attributes/${attrId}/options`, { method: 'POST', body: JSON.stringify(payload) });
export const updateAttributeOption = (attrId: number, optId: number, payload: { name: string }) =>
    request<Partial<ApiAttributeOption> & {id: number}>(`/product-attributes/${attrId}/options/${optId}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteAttributeOption = (attrId: number, optId: number) =>
    request<{ message: string }>(`/product-attributes/${attrId}/options/${optId}`, { method: 'DELETE' });

// ─── Inventory Locations ──────────────────────────────────────────────────────

export interface ApiInventoryLocationInfo {
    id: number;
    code: string;
    name: string;
    is_active: boolean;
    sort_order: number;
}

// ─── Image Tools ─────────────────────────────────────────────────────────────

export type CoverPosterSourceKind = 'original' | 'white_bg' | 'poster';
export type CoverPosterJobStatus =
    | 'queued'
    | 'uploading_input'
    | 'generating_white_bg'
    | 'white_bg_done'
    | 'generating_template'
    | 'template_done'
    | 'generating_cover'
    | 'completed'
    | 'failed'
    | 'cancelled';

export interface ApiCoverPosterJob {
    id: number;
    job_type: 'cover_poster' | 'product_poster';
    status: CoverPosterJobStatus;
    source_kind: CoverPosterSourceKind;
    headline_text: string;
    input_url: string | null;
    white_bg_url: string | null;
    template_url: string | null;
    final_poster_url: string | null;
    prompt_version: string | null;
    error_msg: string | null;
    identity_verification: ApiGeneratedImageIdentityVerification | null;
    created_at: string;
    updated_at: string;
}

export type GeneratedImageIdentityStatus =
    | 'preserved'
    | 'uncertain'
    | 'upstream_uncertain'
    | 'upstream_changed'
    | 'lifestyle_changed'
    | 'verification_unavailable';

export interface ApiGeneratedImageIdentityComparison {
    verdict: 'likely_preserved' | 'uncertain' | 'likely_changed';
    score: number;
    comparison_context: string;
    signals: Record<string, unknown>;
    reason_codes: string[];
    elapsed_ms?: number;
}

export interface ApiGeneratedImageIdentityVerification {
    status: GeneratedImageIdentityStatus;
    algorithm_version: string;
    comparisons?: {
        original_to_white_bg?: ApiGeneratedImageIdentityComparison;
        white_bg_to_lifestyle?: ApiGeneratedImageIdentityComparison;
    };
    reason_codes?: string[];
    diagnostic_code?:
        | 'dependency_unavailable'
        | 'image_processing_failed'
        | 'resource_exhausted'
        | 'internal_error';
}

export interface ApiCoverPosterJobList {
    items: ApiCoverPosterJob[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export type ProductGenerationScope = 'all' | 'lifestyle' | 'poster';
export interface ApiProductPosterJob extends ApiCoverPosterJob {
    generation_scope?: ProductGenerationScope;
    product_master_id: number | null;
}

export const listProductPosters = (productMasterId: number, limit = 10) =>
    request<{ jobs: ApiProductPosterJob[] }>(
        `/products/${productMasterId}/posters?limit=${limit}`,
    );

export interface ApiProductPosterState {
    poster_error?: string | null;
    can_generate?: boolean;
    generation_scope?: ProductGenerationScope;
    ai_image_variants?:ImageVariants|null;
    poster_job_id: number | null;
    poster_status: CoverPosterJobStatus | null;
    has_ai_image: boolean;
    has_complete_ai_set?: boolean;
    ai_image_url: string | null;
}
export const getLivePosterStates = (liveId:number, ids:number[], signal?:AbortSignal) =>
    request<{items:Array<ApiProductPosterState & {product_master_id:number}>}>(
        `/livestreams/${liveId}/poster-jobs?view=fields&product_master_ids=${ids.join(',')}`, {signal}, true);

export const getProductPosterStates = (ids:number[], liveId?:number, signal?:AbortSignal) =>
    liveId == null ? request<{items:Array<ApiProductPosterState & {product_master_id:number}>}>(
        `/products/posters/states?product_master_ids=${ids.join(',')}`, {signal}, true) : getLivePosterStates(liveId,ids,signal);

export interface ApiProductPosterEnqueueResult {
    queued: number;
    skipped: number;
    failed: number;
    results: Array<{
        product_master_id: number;
        status: 'queued' | 'skipped' | 'failed';
        job_id?: number;
        reason_code?: string;
        reason?: string;
        state?: ApiProductPosterState;
    }>;
}

export const enqueueProductPosters = (productMasterIds: number[], options?: {regenerate?:boolean;category_id?:number;scope?:ProductGenerationScope;livestream_id?:number;procurement_line_id?:number}) =>
    request<ApiProductPosterEnqueueResult>(`/products/posters/enqueue${productEditContext(options?.procurement_line_id)}`, {
        method: 'POST',
        body: JSON.stringify({ product_master_ids: productMasterIds, ...options }),
    });
export const cancelProductPosters = (jobs:{product_master_id:number;job_id:number}[],options?:{livestream_id?:number;procurement_line_id?:number}) =>
    request<{cancelled:number;items:(ApiProductPosterState&{product_master_id:number})[]}>(`/products/posters/cancel${productEditContext(options?.procurement_line_id)}`,{method:'POST',body:JSON.stringify({jobs,...options})});
export const cancelCoverPosterJob = (jobId:number) => request<ApiCoverPosterJob>(`/image-tools/cover-jobs/${jobId}/cancel`,{method:'POST'});


const productSaveHeaders = (key?: string) => key ? {'X-Product-Save-Key': key} : undefined;

export async function uploadProductImage(productMasterId: number, photo: File, assetKind: 'white_bg' | 'lifestyle' | 'poster', procurementLineId?: number, saveKey?: string) {
    const form = new FormData();
    form.append('photo', photo);
    form.append('asset_kind', assetKind);
    const res = await erpFetch(`${BASE}/products/${productMasterId}/posters/upload${productEditContext(procurementLineId)}`, {
        method: 'POST',
        headers: productSaveHeaders(saveKey),
        body: await directMediaForm(`${BASE}/products/${productMasterId}/posters/upload${productEditContext(procurementLineId)}`,form),
    });
    if (!res.ok) throw await apiError(res);
    return res.json() as Promise<{ asset_kind: typeof assetKind; url: string; job: ApiProductPosterJob | null }>;
}

// ─── Inventory ────────────────────────────────────────────────────────────────

export interface ApiInventoryLocation {
    warehouse_id: number;
    warehouse_name: string;
    warehouse_code?: string | null;
    location_id: number;
    location_name: string;
    location_code?: string | null;
    on_hand_quantity: number;
    reserved_live_quantity?: number;
    supplier_reserved_quantity?: number;
    live_reserved_quantity?: number;
    shopify_reserved_quantity?: number;
    total_reserved_quantity?: number;
    available_quantity: number;
    incoming_quantity: number;
}

export type ApiSourceImageKind = 'product_photo' | 'poster';

export interface ApiProductImageAsset {
    id: number | null;
    product_master_id?: number;
    role: 'primary' | 'bottom' | 'other' | string;
    source_url?: string | null;
    url?: string | null;
    status?: string | null;
    error?: string | null;
    sort_order?: number;
    legacy?: boolean;
}

export interface ApiProductImages {
    source_original_url?: string | null;
    original_url?: string | null;
    white_bg_url?: string | null;
    lifestyle_url?: string | null;
    poster_url?: string | null;
    bottom_url?: string | null;
}

export interface ApiProductCategorySummary {
    id: number;
    name: string;
}

export interface ApiProductSupplierAssociation {
    id: number;
    product_master_id: number;
    supplier_id: number;
    supplier_name: string | null;
    supplier_sku: string | null;
}

export interface ApiProductTitle {brand_name?:string|null;product_labels?:string[]}
export interface ApiProductSummary {
    image_variants?:ImageVariants|null;
    id: number;
    sku: string;
    name?: string | null;
    display_name?: string | null;
    purchase_url?: string | null;
    print_image_url?: string | null;
    images?: ApiProductImages;
    // Medium, Qiniu-resized signed URLs for overview cards/list thumbnails.
    previews?: ApiProductImages;
    // Small, Qiniu-resized signed URLs (same keys as `images`) for list/grid
    // thumbnails — much smaller payload than the full-res `images`.
    thumbnails?: ApiProductImages;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    unit?: string | null;
    category?: ApiProductCategorySummary | null;
    brand_id?: number | null;
    series_id?: number | null;
    brand?: ApiProductBrand | null;
    product_labels?: string[];
    product_note?: string | null;
    product_note_summary?: string | null;
    aisle?: number | null;
    bin?: number | null;
    storage_location?: number | null;
    source_original_url?: string | null;
    source_image_kind?: ApiSourceImageKind | null;
    original_image_status?: string | null;
    original_image_error?: string | null;
    collect_platform?: string | null;
    collect_url?: string | null;
    collect_sku?: string | null;
    image_assets?: ApiProductImageAsset[];
    attributes?: Record<string, string[]>;
    supplier_attributes?: Record<string,string|string[]|null>;
    supplier_attribute_source?: 'brand'|'series'|'pot'|null;
    supplier_attribute_sources?: Record<string,'brand'|'series'|'pot'>;
    attribute_values?: Array<{
        id: number;
        product_master_id: number;
        attribute_id: number;
        attribute_key?: string | null;
        attribute_name?: string | null;
        option_id: number | null;
        option_name?: string | null;
    }>;
    supplier_sku?: string | null;
    supplier_associations?: ApiProductSupplierAssociation[];
}

export interface ApiProductSale {sale_price?:number|null;sale_currency?:string|null;pricing?:ProductPricing;pricing_input?:ProductPricingInput}

export interface ApiInventoryItem extends Partial<ApiProductPosterState>, ApiProductSale {
    information_complete?:boolean;
    purchased_quantity?:number;
    can_rename?:boolean;
    missing_purchase_fields?:string[];
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    on_hand_quantity: number;
    reserved_live_quantity: number;
    supplier_reserved_quantity?: number;
    supplier_live_quantity?: number;
    supplier_purge_quantity?: number;
    live_reserved_quantity: number;
    shopify_reserved_quantity: number;
    total_reserved_quantity: number;
    available_quantity: number;
    incoming_quantity: number;
    stock_state: string;
    locations: ApiInventoryLocation[];
    product_master?: ApiProductSummary;
}

export interface ApiInventoryList {
    items: ApiInventoryItem[];
    total: number;
    total_quantity?: number | null;
    page: number;
    per_page: number;
    pages: number;
}

export const getInventory = (params?: {
    q?: string;
    location_id?: number | string;
    warehouse_id?: number | string;
    stock_state?: string;
    inventory_view?: string;
    status?: string;
    packaging_status?: string;
    category?: string;
    sort?: string;
    page?: number;
    per_page?: number;
}, options?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams({ view: 'screen',...productSortRates(params?.sort) });
    if (params?.q) q.set('q', params.q);
    if (params?.location_id) q.set('location_id', String(params.location_id));
    if (params?.warehouse_id) q.set('warehouse_id', String(params.warehouse_id));
    if (params?.sort) q.set('sort', params.sort);
    if (params?.inventory_view) q.set('inventory_view', params.inventory_view);
    if (params?.stock_state) q.set('stock_state', params.stock_state);
    if (params?.status) q.set('status', params.status);
    if (params?.packaging_status) q.set('packaging_status', params.packaging_status);
    if (params?.category) q.set('category', params.category);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiInventoryList>(`/inventory${qs ? `?${qs}` : ''}`, options);
};

export const deleteInventoryProducts = (ids:number[]) => request<{deleted:boolean;ids:number[]}>(
    ids.length===1?`/inventory/${ids[0]}`:'/inventory/batch-delete',
    ids.length===1?{method:'DELETE'}:{method:'POST',body:JSON.stringify({ids})});

export interface ApiInventoryDetail {
    information_complete?:boolean;
    missing_purchase_fields?:string[];
    recording_source?: 'supplier'|'team'|'scraper';
    maintenance?: 'withpot' | 'supplier';
    supplier_product_id?: number | null;
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    on_hand_quantity: number;
    available_quantity: number;
    reserved_live_quantity: number;
    supplier_reserved_quantity?: number;
    live_reserved_quantity: number;
    shopify_reserved_quantity: number;
    total_reserved_quantity: number;
    incoming_quantity: number;
    stock_state: string;
    purchase_price?: number | null;
    purchase_currency?: string | null;
    locations: ApiInventoryLocation[];
    product_master: ApiProductSummary;
    procurement_incoming_lines: Array<{
        id: number;
        target_quantity: number;
        received_quantity: number;
        procurement_order: { id: number; name: string };
    }>;
    live_reservations: Array<{
        id: number;
        reserved_quantity: number;
        sold_quantity: number;
        livestream: { id: number; name: string; title?: string | null; status: string };
    }>;
    movement_history: Array<{
        id: number;
        movement_type: string;
        quantity_change: number;
        created_at: string;
        notes?: string;
    }>;
}

/** Authoritative editor fields, without unrelated movement/reservation histories. */
export type ApiProductDetail = Omit<ApiInventoryDetail, 'procurement_incoming_lines' | 'live_reservations' | 'movement_history'> & Partial<ApiProductPosterState> & {
    catalog_sku?: string | null;
    latest_poster_job?: ApiProductPosterJob | null;
};
export const getProductDetail = (productMasterId: number, options?: { signal?: AbortSignal }) =>
    request<ApiProductDetail>(`/inventory/${productMasterId}?view=product`, { signal: options?.signal });

type StockRecordPage<T> = {items:T[];next_cursor:number|null};
export interface ApiProductStockRecords {
    reservations_warehouses: Array<number|null>;
    incoming_warehouses: Array<number|null>;
    supplier_warehouses?: Array<number|null>;
    supplier?: StockRecordPage<{id:number;livestream_id:number;live_number:string|null;title:string|null;supplier_name:string|null;quantity:number;available_quantity:number}>;
    movements: StockRecordPage<{id:number;created_at:string;movement_type:string;on_hand_delta:number;
        reserved_delta:number;available_delta:number;source_type:string|null;source_id:string|null;source_name?:string|null;reason:string;notes:string|null;warehouse_name:string|null}>;
    reservations: StockRecordPage<{id:number;livestream_id:number;live_number:string|null;title:string|null;warehouse_id:number|null;warehouse_name:string|null;quantity:number}>;
    incoming: StockRecordPage<{id:number;order_id:number;order_code:string;order_name:string|null;warehouse_id:number;warehouse_name:string|null;quantity:number}>;
}
export type ProductStockQuery =
    | {movement_before?:number;detail_kind?:never}
    | {detail_kind:'reservations';warehouse_id:number;live_before?:number}
    | {detail_kind:'incoming';warehouse_id:number;incoming_before?:number}
    | {detail_kind:'supplier';warehouse_id:number;supplier_before?:number};
export const getProductStockRecords = (id:number,cursors:ProductStockQuery,signal?:AbortSignal) => {
    const params=new URLSearchParams();
    Object.entries(cursors).forEach(([key,value])=>{if(value!=null)params.set(key,String(value));});
    return request<ApiProductStockRecords>(`/inventory/${id}/records?${params}`,{signal});
};

export const getProductDetailBySku = (sku:string, options?:{signal?:AbortSignal}) =>
    request<ApiProductDetail>(`/inventory/by-sku?${new URLSearchParams({sku})}`,{signal:options?.signal});

export interface ApiCatalogProductSummary {
    id: number;
    sku: string;
    display_name?: string | null;
    images?: ApiProductImages;
    previews?: ApiProductImages;
    thumbnails?: ApiProductImages;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    opening_diameter?: number | null;
    unit?: string | null;
    category?: ApiProductCategorySummary | null;
    attributes?: Record<string, string[]>;
    attribute_values?: ApiProductSummary['attribute_values'];
}

export interface ApiCatalogItem {
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    product_master?: ApiCatalogProductSummary | null;
}

export interface ApiCatalogList {
    items: ApiCatalogItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getCustomerCatalog = (params?: {
    q?: string;
    attribute_option_id?: number;
    page?: number;
    per_page?: number;
}, options?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.attribute_option_id) q.set('attribute_option_id', String(params.attribute_option_id));
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return request<ApiCatalogList>(`/catalog${qs ? `?${qs}` : ''}`, options);
};

export type ImportCorrections = Record<string,Record<string,string>>;

export type ApiStockUpdateField = 'sku' | 'warehouse' | 'quantity';
export type ApiStockUpdateMappings = Partial<Record<ApiStockUpdateField, string>>;
export interface ApiStockUpdateRowError {type:string;message:string}
export interface ApiStockUpdatePreviewRow {
    row_number:number;sku:string;warehouse:string;quantity:number|null;warehouse_id:number|null;product_master_id:number|null;
    previous_available_quantity:number;previous_on_hand_quantity:number;reserved_quantity:number;
    valid:boolean;errors:ApiStockUpdateRowError[];raw_values:Record<string,unknown>;
}
export interface ApiStockUpdatePreview {
    filename:string|null;sheet_name:string|null;detected_columns:string[];proposed_mappings:ApiStockUpdateMappings;
    ignored_columns:string[];row_count:number;valid_count:number;error_count:number;
    expected_quantities:Record<string,number>;row_previews:ApiStockUpdatePreviewRow[];
}
export interface ApiStockUpdateResultRow {
    row_number:number;master:Pick<ApiProductSummary,'id'|'sku'|'name'>;
    inventory:{warehouse_id:number;warehouse_name:string;previous_available_quantity:number;updated_available_quantity:number;
        previous_on_hand_quantity:number;updated_on_hand_quantity:number;reserved_quantity:number;on_hand_delta:number};
}
export interface ApiStockUpdateCommitResult {
    filename:string|null;sheet_name:string|null;row_count:number;updated_count:number;inventory_movement_count:number;results:ApiStockUpdateResultRow[];
}
type StockUpdateUpload={file:File;corrections?:ImportCorrections;mappings?:ApiStockUpdateMappings;sheetName?:string;signal?:AbortSignal};
function stockUpdateForm(payload:StockUpdateUpload) {
    const form=new FormData();form.append('file',payload.file);
    if(payload.corrections)form.append('corrections',JSON.stringify(payload.corrections));
    if(payload.mappings)form.append('mappings',JSON.stringify(payload.mappings));
    if(payload.sheetName)form.append('sheet_name',payload.sheetName);
    return form;
}
export async function previewStockUpdate(payload:StockUpdateUpload):Promise<ApiStockUpdatePreview> {
    return (await erpFormRequest<{data:ApiStockUpdatePreview}>('/inventory/stock-updates/preview',stockUpdateForm(payload),undefined,payload.signal)).data;
}
export async function commitStockUpdate(payload:StockUpdateUpload & {expectedQuantities:Record<string,number>;requestKey:string}):Promise<ApiStockUpdateCommitResult> {
    const form=stockUpdateForm(payload);form.append('expected_quantities',JSON.stringify(payload.expectedQuantities));
    return (await erpFormRequest<{data:ApiStockUpdateCommitResult}>('/inventory/stock-updates/commit',form,{'X-Product-Save-Key':payload.requestKey},payload.signal)).data;
}
export async function downloadStockUpdateTemplate() {
    const response=await erpFetch(`${BASE}/inventory/stock-updates/template`);
    if(!response.ok)throw await apiError(response);
    const url=URL.createObjectURL(await response.blob()),a=document.createElement('a');
    a.href=url;a.download='WITHPOT_批量更新库存模板.xlsx';a.click();URL.revokeObjectURL(url);
}

// ─── Product Masters / Imports ───────────────────────────────────────────────

export type ApiProductImportField =
    | 'sku'
    | 'name'
    | 'brand'
    | 'original_url'
    | 'bottom_image_url'
    | 'other_image_urls'
    | 'purchase_url'
    | 'product_labels'
    | 'source_image_kind'
    | 'height'
    | 'width'
    | 'depth'
    | 'internal_height'
    | 'internal_width'
    | 'internal_depth'
    | 'weight'
    | 'opening_diameter'
    | 'unit'
    | 'aisle'
    | 'bin_number'
    | 'quantity'
    | 'location_id'
    | 'location_code'
    | 'purchase_price'
    | 'supplier'
    | 'supplier_sku'
    | 'product_attributes';

// ─── Livestreams ──────────────────────────────────────────────────────────────

// 直播三态（+ 已取消）：选品中 pending（编排/推送采购）· 直播中 live（开始贴标后）·
// 已完成 completed（完成直播后）· 已取消 cancelled。
export type LivestreamStatus = 'pending' | 'live' | 'completed' | 'cancelled';

export interface ApiLivestreamStoryMetadata {
    // TODO: Future deprecation. Backend keeps historical story labels for
    // compatibility, but new frontend surfaces should hide this field.
    label?: string;
    page_break_before?: boolean;
    [key: string]: unknown;
}

export interface ApiLivestreamSelectionMetadata {
    return_sellable_quantity?:number;
    tags: string[];
    ai_plan?: {
        item_role?: string;
        topic_segment?: string;
        short_reason?: string;
        comment?: string;
        [key: string]: unknown;
    };
    story?: ApiLivestreamStoryMetadata;
    [key: string]: unknown;
}

// A 采购 that contains this product at the live's location (informational
// provenance; a product can have several sources).
export interface ApiProcurementSource {
    procurement_order_id: number;
    order_name: string | null;
    order_code: string;
    order_status: string;
    line_status: string;
    stage: ItemReadiness;
    target_quantity: number;
    purchased_quantity: number;
    received_quantity: number;
    // Optional 采购数量限制 (lower/upper bound) to guide the live quantity.
    supplier_name: string | null;
}

export interface ApiLivestreamItem extends ProductPricing {
    export_ordinal?:number;
    // Unlocked selection has no customer-facing number.
    export_position?: number | null;
    missing_purchase_fields?:string[];
    id: number;
    product_master_id: number;
    group_id?: number | null; // per-直播 分组 (ErpLivestreamGroup). null = 未分组.
    reserved_quantity: number;
    sold_quantity: number;
    sort_order: number;
    custom_sort_order: number;
    source?: 'product_library' | 'procurement' | 'order_return';
    product_name: string;
    product_master?: ApiProductSummary;
    final_listing_price?: number | null;
    final_listing_price_currency?: string | null;
    selection_metadata?: ApiLivestreamSelectionMetadata;
    supplier_id?: number | null;
    supplier_name?: string | null;
    supplier_brand_story?: string | null;
    // 预排 readiness, relative to this item's claim at the live's location.
    reserved_fulfilled_quantity?: number;
    readiness?: ItemReadiness;
    location_on_hand_quantity: number;
    sellable_quantity?: number;
    location_available_quantity?: number;
    location_incoming_quantity?: number;
    planned_shortfall_quantity?: number;
    outstanding_quantity?: number;
    // 在库 shortfall (claim minus on-hand backing); stock_warning is true only
    // inside the pre-live window when this item's claim exceeds 在库 stock.
    stock_shortfall_quantity?: number;
    stock_warning?: boolean;
    // Source 采购(s) this product appears in (provenance, may be several).
    procurement_sources?: ApiProcurementSource[];
}

// One 场次 (airing) of a livestream: its own date/time + 主播 + 导播 + 回传状态.
export interface ApiLivestreamSession {
    reservation_url?: string | null;
    media?: Record<string, LiveWorkValue>;
    giveaway_item_id?: number | null;
    giveaway_name?: string | null;
    giveaway_product_id?: number | null;
    id: number;
    livestream_id: number;
    scheduled_at: string | null;
    streamer_id: number | null;
    streamer_name: string | null;
    director_id: number | null;
    director_name: string | null;
    returned_at: string | null;
    is_returned: boolean;
    has_reviewed_order?: boolean;
    returned_import_id: number | null;
    /** 本场次所属回传批次的件数 / 订单行数：发货时出库的数量。 */
    returned_quantity?: number | null;
    returned_line_count?: number | null;
    sort_order: number;
}

// Input shape for creating/updating a 场次 inline via the livestream payload.
export interface LivestreamSessionInput {
    reservation_url?: string | null;
    giveaway_item_id?: number | null;
    id?: number;
    scheduled_at: string | null;
    streamer_id?: number | null;
    director_id?: number | null;
}

export interface ApiLivestreamGroup {
    id: number;
    livestream_id: number;
    name: string;
    description?: string | null;
    sort_order: number;
    item_count: number;
    reserved_quantity?: number;
    created_at?: string | null;
    updated_at?: string | null;
}

export type LivestreamSeries = string;

export interface ApiLivestream {
    reservation_url?: string | null;
    scheduled_at?:string|null; media?:Record<string,LiveWorkValue>; returned_at?:string|null; returned_import_id?:number|null;
    has_reviewed_order?:boolean;
    document_sessions?:Array<{id:number;is_returned?:boolean;has_reviewed_order?:boolean}>;
    picking_views?:Array<'selection'|'purchase'>;
    supplier_collaboration?:SupplierCollaboration|null; picking_suppliers?:Array<{id:number;name:string}>;
    id: number;
    live_code: string;
    item_sort_mode?: LivestreamItemSortMode;
    groups?: ApiLivestreamGroup[]; // per-直播 分组, empty for a fresh live.
    name: string;
    title?: string | null;
    description: string;
    product_pitch_script?: string | null;
    packaging_title?: string | null;
    pinned_comment?: string | null;
    live_note?: string | null;
    sessions?: ApiLivestreamSession[];
    streamer_id: number | null;
    streamer_name: string;
    series_type?: 'live' | 'purge';
    series_type_selected?: boolean;
    series?: LivestreamSeries | null;
    supplier_id?: number | null;
    supplier_ids?: number[];
    brand_ids?:number[];
    brands?:{id:number;name:string}[];
    suppliers?: Array<{id:number;name:string}>;
    supplier_name?: string | null;
    live_type: string;
    status: LivestreamStatus;
    location_id: number | null;
    location_code: string | null;
    location_name: string | null;
    warehouse_id: number | null;
    warehouse_code: string | null;
    warehouse_name: string | null;
    shipping_adjustment_usd: number;
    item_count: number;
    reserved_quantity: number;
    sold_quantity: number;
    sellable_item_count?: number;
    sellable_quantity?: number;
    sold_item_count?: number;
    // True when, within ~1 week of the earliest scheduled session, some item's claim exceeds 在库 stock.
    has_stock_warning?: boolean;
    // True once a 采购 has been generated (推送采购 / 开始贴标). Copy/move items is
    // only allowed before this.
    has_generated_procurement?: boolean;
    final_price_item_count?: number;
    priced_item_count?: number;
    margin_item_count?: number;
    estimated_revenue?: number | null;
    estimated_cost?: number | null;
    estimated_profit_amount?: number | null;
    estimated_margin_percentage?: number | null;
    actual_revenue?: number | null;
    actual_cost?: number | null;
    actual_profit_amount?: number | null;
    actual_margin_percentage?: number | null;
    actual_sold_quantity?: number;
    actual_order_count?: number;
    actual_line_count?: number;
    actual_import_status?: string | null;
    latest_order_import?: ApiLivestreamOrderImportSummary | null;
    order_imports?: ApiLivestreamOrderImportSummary[];
    items?: ApiLivestreamItem[];
    settled_at?: string | null;
    // Set once this live's 未售 leftover has been released to another live (one-time).
    is_settled?: boolean;
    created_at: string;
    updated_at: string;
}

export interface ApiLivestreamOrderImportSummary {
    id: number;
    platform: string;
    filename: string | null;
    selected_sheet: string;
    status: string;
    row_count: number;
    matched_count: number;
    error_count: number;
    total_quantity: number;
    total_revenue: number | null;
    currency: string | null;
    committed_at: string | null;
    created_at: string | null;
}

export interface ApiLivestreamOrderImportRow {
    append_to_live?: boolean;
    row_number?: number;
    row_numbers?: number[];
    order_numbers?: string[];
    order_number?: string | null;
    order_date?: string | null;
    platform_username?: string | null;
    recipient_name?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postal_code?: string | null;
    normalized_sku?: string | null;
    sku?: string | null;
    uploaded_sku?: string | null;
    raw_item_title?: string | null;
    quantity?: number;
    unit_sale_price?: string;
    currency?: string | null;
    order_status?: string | null;
    livestream_item_id?: number | null;
    product_master_id?: number;
    product_name?: string | null;
    reserved_quantity?: number;
    current_sold_quantity?: number;
    error_kind?: string;
    error?: string;
    existing_line_id?: number;
    previous_quantity?: number;
    previous_unit_sale_price?: string | null;
}

export interface ApiLivestreamOrderImportItemEffect {
    livestream_item_id: number;
    product_master_id: number;
    sku?: string | null;
    product_name?: string | null;
    current_sold_quantity: number;
    proposed_sold_quantity: number;
    sold_delta: number;
    reserved_quantity: number;
}

export interface OrderAllocation {sku:string; quantity:number; product_master_id?:number | null; fulfilled_quantity?:number; shortage_quantity?:number}
export interface OrderReturnDraft {
    row_number:number; order_number:string; order_date:string | null; platform_username:string;
    raw_item_title:string; uploaded_sku:string; platform_quantity:number | null; amount:string | null; currency:string;
    complete:boolean; allocations:OrderAllocation[]; errors:string[]; shortage_quantity:number;
}
export interface ApiLivestreamOrderImportPreview {
    orders?:OrderReturnDraft[]; revision?:number;
    import: ApiLivestreamOrderImportSummary;
    valid: boolean;
    matched_rows: ApiLivestreamOrderImportRow[];
    new_rows: ApiLivestreamOrderImportRow[];
    updated_rows: ApiLivestreamOrderImportRow[];
    unchanged_rows: ApiLivestreamOrderImportRow[];
    unmatched_rows: ApiLivestreamOrderImportRow[];
    duplicate_rows: ApiLivestreamOrderImportRow[];
    invalid_rows: ApiLivestreamOrderImportRow[];
    non_usd_rows: ApiLivestreamOrderImportRow[];
    quantity_over_reserved_rows: ApiLivestreamOrderImportRow[];
    below_current_sold_rows: ApiLivestreamOrderImportRow[];
    affected_item_summaries: ApiLivestreamOrderImportItemEffect[];
    totals: {
        row_count: number;
        matched_count: number;
        error_count: number;
        total_quantity: number;
        total_revenue: number | null;
        currency: string | null;
    };
}

export type ApiLivestreamItemConfirmation = Pick<ApiLivestreamItem, 'id'|'product_master_id'|'group_id'|'reserved_quantity'|'reserved_fulfilled_quantity'|'sold_quantity'|'final_listing_price'|'final_listing_price_currency'>;
export type ApiLivestreamConfirmation = Pick<ApiLivestream, 'id'|'name'|'title'|'live_code'|'status'|'live_type'|'series_type'|'warehouse_id'|'location_id'|'settled_at'|'is_settled'> & {sessions?: Pick<ApiLivestreamSession,'id'|'returned_at'|'returned_import_id'|'is_returned'>[]};

/** 本次新增履约量；newly_added 区分本次加入直播与已有商品增量。 */
export type ApiTemporaryLabel = {item_id:number; quantity:number; newly_added?:boolean};
export interface ApiLivestreamOrderImportCommitResult {
    import: ApiLivestreamOrderImportSummary;
    livestream: ApiLivestreamConfirmation;
    temporary_labels?: ApiTemporaryLabel[];
    /** Stock taken from other lives' reservations for goods sold here (情况 3). */
    borrowed_from?: Array<{ livestream_id: number; livestream_name: string | null; sku: string | null; quantity: number }>;
    summary_email_task?: {
        id: number;
        status: string;
        created: boolean;
    };
}

export interface ApiLivestreamAiPlanApplyResult {
    livestream: ApiLivestreamConfirmation;
    added_count: number;
    updated_count: number;
    metadata_count: number;
    reordered_count: number;
    cover_item_id: number | null;
}

export interface ApiLivestreamList {
    items: ApiLivestream[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export type ApiLivestreamSummary = Pick<ApiLivestream, 'has_reviewed_order' | 'picking_views' | 'brand_ids' | 'scheduled_at' | 'supplier_collaboration' | 'document_sessions' | 'picking_suppliers' | 'sellable_item_count' | 'sellable_quantity' | 'sold_item_count' | 'id' | 'live_code' | 'name' | 'title' | 'status' | 'live_type' | 'series_type' | 'item_count' | 'reserved_quantity' | 'sold_quantity' | 'estimated_revenue' | 'estimated_margin_percentage' | 'actual_revenue' | 'actual_margin_percentage' | 'actual_sold_quantity' | 'actual_import_status'> & {has_complete_ai_set?:boolean;cover_url?:string|null;warehouse_name?:string|null;warehouse_code?:string|null};
export const getLivestreamSummaries = (params?: {
    sharedView?: boolean;
    viewOptions?: Record<string,string>;
    unscheduledOnly?: boolean;
    status?: string;
    warehouse_id?: number | string;
    member_id?: number | string;
    streamer_id?: number;
    director_id?: number;
    month?: string;
    live_type?: string;
    series_type?: 'live' | 'purge';
    q?: string;
    search_time_zone?: string;
    sort?: string;
    page?: number;
    per_page?: number;
}, opts?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams({ view: 'summary' });
    if (params?.unscheduledOnly) q.set('unscheduled_only','1');
    if(params?.sharedView){q.set('shared_view','1');q.set('view_options',JSON.stringify(params.viewOptions ?? {}));}
    if (params?.search_time_zone) q.set('search_time_zone', params.search_time_zone);
    if (params?.status) q.set('status', params.status);
    if (params?.live_type) q.set('live_type', params.live_type);
    if (params?.series_type) q.set('series_type', params.series_type);
    if (params?.q) q.set('q', params.q);
    if (params?.sort) q.set('sort', params.sort);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.warehouse_id) q.set('warehouse_id', String(params.warehouse_id));
    if (params?.member_id) q.set('member_id', String(params.member_id));
    if (params?.streamer_id) q.set('streamer_id',String(params.streamer_id));
    if (params?.director_id) q.set('director_id',String(params.director_id));
    if (params?.month) q.set('month',params.month);
    const qs = q.toString();
    return request<Omit<ApiLivestreamList, 'items'> & { items: ApiLivestreamSummary[]; sections?:{key:string;title:string;start:number;count:number}[]; view_settings?: Record<string,string> }>(`/livestreams${qs ? `?${qs}` : ''}`, { signal: opts?.signal });
};

/** Authoritative edit fields; excludes product and financial workspaces. */
export type ApiLivestreamEdit = Pick<ApiLivestream, 'has_reviewed_order' | 'reservation_url' | 'scheduled_at' | 'media' | 'returned_at' | 'returned_import_id' | 'sellable_item_count' | 'sellable_quantity' | 'sold_item_count' | 'id' | 'name' | 'title' | 'warehouse_id' | 'series_type' | 'series_type_selected' | 'series' | 'supplier_id' | 'supplier_name' | 'supplier_ids' | 'brand_ids' | 'brands' | 'suppliers' | 'description' | 'product_pitch_script' | 'packaging_title' | 'pinned_comment' | 'live_note' | 'status' | 'is_settled' | 'sessions' | 'sold_quantity' | 'live_type'> & {procurement_started?: boolean; warehouse_name?:string|null; warehouse_code?:string|null; item_count?:number; reserved_quantity?:number; loaded_sections?: ('information' | 'scripts' | 'sessions')[]};
export const createLiveSession = (id: number, payload: LivestreamSessionInput) => request<ApiLivestreamSession>(`/livestreams/${id}/sessions`, {method:'POST', body:JSON.stringify(payload)});
export const getLiveGiveawayOptions = (id:number,signal?:AbortSignal,q='') => request<{id:number;name:string;sku:string}[]>(`/livestreams/${id}/giveaway-options?q=${encodeURIComponent(q)}`,{signal});
export const updateLiveSession = (id: number, sessionId: number, payload: Partial<LivestreamSessionInput>) => request<ApiLivestreamSession>(`/livestreams/${id}/sessions/${sessionId}`, {method:'PATCH', body:JSON.stringify(payload)});
export const deleteLiveSession = (id: number, sessionId: number) => request<{id:number; deleted:boolean}>(`/livestreams/${id}/sessions/${sessionId}`, {method:'DELETE'});
export const getLivestreamSection = (id: number, section: 'information' | 'sessions', signal?: AbortSignal) =>
    request<Partial<ApiLivestreamEdit>>(`/livestreams/${id}?view=${section}`, {signal});
export const updateLivestreamFields = (id: number, payload: Partial<{reservation_url:string|null;expected_scheduled_at:null;scheduled_at:string|null;name: string; title: string; warehouse_id: number | null; series_type: 'live' | 'purge'; series: LivestreamSeries | null; supplier_id:number|null; supplier_ids:number[];brand_ids:number[]}>) =>
    request<Partial<ApiLivestreamEdit>>(`/livestreams/${id}?view=fields`, {method:'PATCH', body:JSON.stringify(payload)});

export type ApiLivestreamExport = Pick<ApiLivestream, 'id' | 'name' | 'title' | 'live_code' | 'status' | 'live_type' | 'series_type' | 'scheduled_at' | 'description' | 'product_pitch_script' | 'items'> & {
    order_group_by?:string;order_revision?:number;order_edit_revision?:number;
    groups: Pick<ApiLivestreamGroup, 'id' | 'name' | 'description' | 'sort_order'>[];
    has_reviewed_order?:boolean;
    sessions: Pick<ApiLivestreamSession, 'id' | 'scheduled_at' | 'streamer_name' | 'director_name' | 'is_returned' | 'has_reviewed_order'>[];
};
export const getLivestreamExport = async (id: number, signal?: AbortSignal, selection?:{itemIds?:readonly number[];positions?:readonly number[]},order?:{sort?:string;groupBy?:string}):Promise<ApiLivestreamExport> => {
    const chosen=selection?.itemIds??selection?.positions;
    if(chosen&&chosen.length>1000) {
        let result:ApiLivestreamExport|undefined;
        // Two independent bounded reads at a time avoid a long serial wait
        // without flooding the shared database pool for large selections.
        for(let offset=0;offset<chosen.length;offset+=2000) {
            const ranges=[chosen.slice(offset,offset+1000),chosen.slice(offset+1000,offset+2000)].filter(range=>range.length);
            const batch=await Promise.all(ranges.map(range=>getLivestreamExport(id,signal,selection?.itemIds?{itemIds:range}:{positions:range},order)));
            for(const next of batch){if(result&&result.order_revision!==next.order_revision)throw new Error('排序已变化，请重新导出');result=result?{...next,items:[...(result.items??[]),...(next.items??[])]}:next;}
        }
        return {...result!,items:[...(result!.items??[])].sort((a,b)=>(a.export_ordinal??0)-(b.export_ordinal??0))};
    }
    const params=new URLSearchParams({view:'export'});
    if(order?.sort)params.set('sort',order.sort);
    if(order?.groupBy)params.set('group_by',order.groupBy);
    if(selection?.itemIds)params.set('item_ids',selection.itemIds.join(','));
    else if(selection?.positions)params.set('positions',selection.positions.join(','));
    return request<ApiLivestreamExport>(`/livestreams/${id}?${params}`, {signal}, true);
};

export interface LivestreamSummaryEmailTaskResult {
    task: {
        id: number;
        status: string;
        content_hash: string;
        subject: string;
    };
    created: boolean;
    content_hash: string;
}

// Mark one 场次 as 回传'ed (locks its date/host/director) or clear it.
export const duplicateLivestream = (id: number) =>
    request<ApiLivestreamEdit>(`/livestreams/${id}/duplicate?view=fields`, { method: 'POST' });

// 推送采购 (repeatable, pre-confirm): reserve stock + create/reconcile the shortfall
// 采购 without locking the live. See docs/live-first-procurement.md §6b.
export type ApiLivestreamWorkflowResult = Pick<ApiLivestream, 'id' | 'name' | 'title' | 'status'> & {needs_procurement?:boolean;item_count?:number;reserved_quantity?:number};
export const pushLivestreamProcurement = (id: number) =>
    request<ApiLivestreamWorkflowResult>(
        `/livestreams/${id}/push-procurement?view=workflow`, { method: 'POST' },
    );

export const previewUnpushedLivestreamSelection = (id:number) =>
    request<{token:string;has_changes:boolean}>(`/livestreams/${id}/unpushed-selection`);
export const discardUnpushedLivestreamSelection = (id:number,token:string) =>
    request<ApiLivestreamWorkflowResult>(`/livestreams/${id}/unpushed-selection`,{method:'POST',body:JSON.stringify({token})});

export const confirmLivestreamGenerate = (id: number) =>
    request<ApiLivestreamWorkflowResult>(
        `/livestreams/${id}/confirm-generate?view=workflow`, { method: 'POST', body:JSON.stringify({view_options:{sort:'added:desc',groupBy:'custom',...readListSettings(`live-view:${id}`)}}) },
    );

export const getLivestreamCompletionReadiness = (id: number) =>
    request<{can_complete:boolean;reason:string|null}>(`/livestreams/${id}/completion-readiness`);

export const settleLivestream = (id: number, finalize = false) =>
    request<{ livestream: ApiLivestreamConfirmation }>(`/livestreams/${id}/${finalize ? 'confirm-orders-returned' : 'complete'}`, {
        method: 'POST',
        body: JSON.stringify({}),
    });

export interface ApiBulkRemoveLivestreamItemsResult {
    livestream: ApiLivestreamConfirmation & {item_count?:number;reserved_quantity?:number;sold_quantity?:number};
    removed_count: number;
    removed_items?: ApiLivestreamItem[];
}

export const bulkRemoveLivestreamItems = (liveId: number, itemIds?: number[]) =>
    request<ApiBulkRemoveLivestreamItemsResult>(
        `/livestreams/${liveId}/items/bulk-remove`,
        { method: 'POST', body: JSON.stringify(itemIds ? { item_ids: itemIds } : {}) },
    );

// Only the source must be unpushed; both series must be in 选品中.
export interface ApiLivestreamTransferTarget {
    title?: string | null;
    eligible_ids?:number[];
    id: number;
    name: string;
    live_code: string;
    live_type: string;
    item_count: number;

}

export const listLivestreamTransferTargets = (liveId: number, itemId?: number, signal?: AbortSignal, groupKey?: string, search?:{q:string;search_time_zone:string;item_ids?:string;mode?:'copy'|'move'}) =>
    request<{ items: ApiLivestreamTransferTarget[]; source_eligible: boolean }>(
        `/livestreams/${liveId}/transfer-targets?${new URLSearchParams({...search,...(groupKey!==undefined?{group_id:groupKey}:itemId==null?{}:{item_id:String(itemId)})})}`, {signal},
    );

export type ApiLivestreamTransferBoard = {source_eligible:boolean; targets:string[]; items:Record<string,string[]>; groups:Record<string,string[]>};
export const getLivestreamTransferBoard = (liveId:number, signal?:AbortSignal) =>
    request<ApiLivestreamTransferBoard>(`/livestreams/${liveId}/transfer-targets?view=drag`, {signal});

export interface ApiLivestreamTransferResult {
    mode: 'copy' | 'move';
    group_moved?: boolean;
    copied_count: number;
    transferred_item_ids?: number[];
    created_item_ids?: number[];
    skipped_item_ids?: number[];
    removed_item_ids?: number[];
    skipped_count: number;
    removed_count: number;
    target: { id: number; name: string };
    livestream?: ApiLivestream;
}

export const transferLivestreamItems = (
    liveId: number,
    payload: { target_livestream_id: number; item_ids?: number[]; group_id?: number | null; mode: 'copy' | 'move'; compact?: boolean },
) =>
    request<ApiLivestreamTransferResult>(`/livestreams/${liveId}/transfer-items`, {
        method: 'POST', body: JSON.stringify(payload),
    });

export const getLivestreamGroups = (liveId: number, signal?: AbortSignal) =>
    request<ApiLivestreamGroup[]>(`/livestreams/${liveId}/groups`, {signal});

export const createLivestreamGroup = async (liveId: number, name: string, description = '') => {
    validateGroupText(name,'分组名称');validateGroupText(description,'分组描述');
    return request<ApiLivestreamGroup>(`/livestreams/${liveId}/groups`, {
        method: 'POST', body: JSON.stringify({ name, description }),
    });
};

export const updateLivestreamGroup = async (liveId: number, groupId: number, name?: string, description?: string) => {
    validateGroupText(name,'分组名称');validateGroupText(description,'分组描述');
    return request<ApiLivestreamGroup>(`/livestreams/${liveId}/groups/${groupId}`, {
        method: 'PATCH', body: JSON.stringify({ name, description }),
    });
};

export const deleteLivestreamGroup = (liveId: number, groupId: number) =>
    request<{ deleted: boolean; group_id: number }>(`/livestreams/${liveId}/groups/${groupId}`, {
        method: 'DELETE',
    });

export const deleteLivestreamGroups = (liveId: number, groupIds: number[]) =>
    request<{ deleted: boolean; group_ids: number[] }>(`/livestreams/${liveId}/groups`, {
        method: 'DELETE', body: JSON.stringify({group_ids: groupIds}),
    });

/** Compact group ordering acknowledgement; quantities and item details do not change. */
export const saveLivestreamGroupOrder = (liveId: number, groupIds: number[]) =>
    request<{group_ids:number[]}>(`/livestreams/${liveId}/groups/reorder`, {
        method:'PATCH',body:JSON.stringify({group_ids:groupIds,compact:true}),
    });

export const assignLivestreamGroupItems = (
    liveId: number,
    itemIds: number[],
    groupId: number | null,
    targetIndex?: number,
) => request<{id:number; items:Pick<ApiLivestreamItem,'id'|'group_id'|'sort_order'|'custom_sort_order'>[]}>(`/livestreams/${liveId}/groups/assign`, {
    method: 'PATCH',
    body: JSON.stringify({
        item_ids: itemIds,
        group_id: groupId,
        ...(targetIndex == null ? {} : { target_index: targetIndex }),
    }),
});

export const createAndAssignLivestreamGroup = async (liveId:number,itemIds:number[],name:string) => {
    validateGroupText(name,'分组名称');
    return request<{id:number;group:ApiLivestreamGroup;items:Pick<ApiLivestreamItem,'id'|'group_id'|'sort_order'|'custom_sort_order'>[]}>(`/livestreams/${liveId}/groups/assign`, {
        method:'PATCH',body:JSON.stringify({item_ids:itemIds,new_group_name:name}),
    });
};


export const deleteLivestream = (id: number) =>
    request<{ deleted: boolean; livestream_id: number }>(`/livestreams/${id}?view=fields`, { method: 'DELETE' });

export interface OrderIntakeResult {livestream_id?:number;session_id?:number|null;returned_at?:string|null;import_id:number;added:number;updated:number;skipped:number;skipped_rows:number[];total:number}
export async function importLivestreamOrders(liveId:number,sessionId:number|null,file:File,requestKey:string,signal?:AbortSignal):Promise<OrderIntakeResult> {
    const form=new FormData();form.append('file',file);if(sessionId!=null)form.append('session_id',String(sessionId));form.append('request_key',requestKey);
    const response=await erpFetch(`${BASE}/livestreams/${liveId}/order-imports`,{method:'POST',body:form,signal});
    if(!response.ok)throw await apiError(response);return response.json();
}

export async function previewLivestreamOrderImport(
    liveId: number,
    file: File,
    sheetName?: string,
    sessionId?: number
): Promise<ApiLivestreamOrderImportPreview> {
    const form = new FormData();
    form.append('file', file);
    if (sheetName) form.append('sheet_name', sheetName);
    if (sessionId != null) form.append('session_id', String(sessionId));
    const res = await erpFetch(`${BASE}/livestreams/${liveId}/order-imports/preview`, {
        method: 'POST',
        body: form,
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

export const getOrderReturnDraft = (liveId:number,sessionId:number) => request<ApiLivestreamOrderImportPreview | null>(`/livestreams/${liveId}/order-imports/draft?session_id=${sessionId}`);
export const getOrderReturnSelection = (liveId:number,sessionId:number,signal?:AbortSignal) =>
    request<{target:ReturnedOrderFilter & {status:LivestreamStatus;warehouse_id:number;returned_at:string|null;returned_import_id:number|null;streamer_id:number|null;director_id:number|null};draft:ApiLivestreamOrderImportPreview|null}>(`/livestreams/${liveId}/order-imports/draft?session_id=${sessionId}&view=selection`,{signal});
export const editOrderReturnDraft = (liveId:number, importId:number, revision:number, order:Pick<OrderReturnDraft,'row_number'|'order_number'|'amount'|'currency'|'allocations'|'complete'>) =>
    request<ApiLivestreamOrderImportPreview>(`/livestreams/${liveId}/order-imports/${importId}`,{method:'PATCH',body:JSON.stringify({...order,revision})});
export interface OrderProductChoice extends ApiProductTitle, ApiProductSale,Partial<ApiProductPosterState> {supplier_reservations?:boolean;missing_purchase_fields?:string[];can_rename?:boolean;id:number;sku:string;name:string | null;thumbnail_url?:string|null;image_variants?:ImageVariants|null;on_hand_quantity:number;available_quantity:number}
export const getOrderReturnProducts = (liveId:number,page:number,q:string,signal?:AbortSignal,replacement?:{orderId:number;sku:string;quantity:number},exactSku?:string) =>
    request<{items:OrderProductChoice[];total:number;pages:number;sku_exists?:boolean|null}>(`/livestreams/${liveId}/order-imports/products?${new URLSearchParams({page:String(page),q,...(exactSku?{exact_sku:exactSku}:{}),...(replacement?{order_id:String(replacement.orderId),...(listingRow(replacement.sku)?{replacing_listing_row:String(listingRow(replacement.sku))}:{replacing_sku:replacement.sku}),minimum_quantity:String(replacement.quantity)}:{})})}`,{signal});
export async function downloadOrderReturnTemplate() {
    const response=await erpFetch(`${BASE}/order-imports/template`);
    if(!response.ok) throw await apiError(response);
    const url=URL.createObjectURL(await response.blob());const a=document.createElement('a');a.href=url;a.download='Palmstreet-orders.xlsx';a.click();URL.revokeObjectURL(url);
}

export const commitLivestreamOrderImport = (liveId: number, importId: number, sessionId: number) =>
    request<ApiLivestreamOrderImportCommitResult>(`/livestreams/${liveId}/order-imports/commit`, {
        method: 'POST', body: JSON.stringify({ import_id: importId, session_id: sessionId }),
    });

export interface ApiSelectableItem {
    purchase_ready?: boolean;
    missing_purchase_fields?: string[];
    id: number;
    product_master_id: number;
    sku: string;
    product_name: string;
    available_quantity: number;
    incoming_quantity: number;
    on_hand_quantity: number;
    base_cost: number | null;
    base_cost_currency: string | null;
    product_master?: ApiProductSummary;
    // Confirmed pending-purchase quantity here; broader planning total in preplan.
    planned_quantity?: number;
    readiness?: ItemReadiness;
}

// How backed a product/claim is, in ascending order of readiness.
export type ItemReadiness = 'planned' | 'purchased' | 'received';

export interface ApiSelectableList {
    items: ApiSelectableItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

// 货盘选品: supplier-pool (SupplierProduct) items, not-yet-digitized (no SKU/stock).
// Selecting one digitizes it into a real SKU + adds to the live as a to-purchase item.
export interface ApiPoolSelectableItem {
    source: 'pool';
    supplier_product_id: number;
    product_master_id: null;
    name: string;
    sku: string;
    supplier_id: number;
    supplier_name: string | null;
    price: number | null;
    price_currency: string | null;
    height: number | null;
    width: number | null;
    depth: number | null;
    weight: number | null;
    photo_url: string | null;
    product_labels: string[];
    available_quantity: number;
    incoming_quantity: number;
    brand_name?: string | null;
    /** false: 货盘资料不全，暂不能下单给供应商（见 missing_purchase_fields）。 */
    purchase_ready?: boolean;
    missing_purchase_fields?: string[];
}
export interface ApiPoolSelectableList {
    items: ApiPoolSelectableItem[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}
// ── 从花盆库选择（手动采购） ─────────────────────────────────────────────
export interface ApiPurchaseSelectableItem {
    purchase_ready?: boolean;
    missing_purchase_fields?: string[];
    source: 'inventory';
    product_master_id: number;
    sku: string;
    name: string;
    product_master?: ApiProductSummary;
    /** 目标仓库的可用 / 在库数量。 */
    available_quantity: number;
    on_hand_quantity: number;
    /** 上次采购的供应商和单价（加入采购时沿用）。 */
    supplier_id: number | null;
    supplier_name: string | null;
    purchase_price: number | null;
    purchase_currency: string;
}
const pickerQuery = (params?: { q?: string; page?: number; per_page?: number }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const qs = q.toString();
    return qs ? `?${qs}` : '';
};
export const addProcurementLibraryLines = (orderId: number, items: Array<
    { product_master_id: number; quantity: number } | { supplier_product_id: number; quantity: number }
>) => request<{ created_count: number; skipped_count: number; line_ids: number[] }>(
    `/procurements/${orderId}/lines/from-library`, { method: 'POST', body: JSON.stringify({ items }) });

export const addLivestreamPoolItem = (liveId: number, supplierProductId: number, quantity: number) =>
    request<ApiLivestreamItemConfirmation>(`/livestreams/${liveId}/items/from-pool`, {
        method: 'POST',
        body: JSON.stringify({ supplier_product_id: supplierProductId, quantity }),
    });

export const bulkAddLivestreamItems = (liveId: number, items: { product_master_id: number; quantity: number }[]) =>
    request<{ livestream: ApiLivestreamConfirmation; items: Pick<ApiLivestreamItem, 'id'|'product_master_id'|'reserved_quantity'>[] }>(`/livestreams/${liveId}/items/bulk-add`, { method: 'POST', body: JSON.stringify({ items }) });

// Reorder returns a lightweight confirmation (not the full livestream) — the
// client already holds the new order optimistically.
export interface ApiLivestreamReorderResult {
    id: number;
    status: LivestreamStatus;
    item_sort_mode: LivestreamItemSortMode;
    item_order: { id: number; sort_order: number; custom_sort_order: number }[];
}

export type LivestreamItemSortMode =
    | 'custom'
    | 'diameter_asc'
    | 'diameter_desc'
    | 'price_asc'
    | 'price_desc';


export interface PrintDispatchResult {
    success: boolean;
    sent: number;
    failed: number;
    total: number;
    jobs?: {error_msg?: string | null}[];
    summary_email_task?: LivestreamSummaryEmailTaskResult;
    summary_email_error?: string;
}

// One label's data — the frontend renders each to an image, then dispatches.
interface ApiPrintLabelBase {
    storage_location?: number | null;
    price_currency?: string;
    sku: string;
    price: number | null;
    livestream_name: string;
    number: number | null; // 序号 in the live; null = 无关联场次 (blank)
    dimensions?: string; // 外部 长宽高 "L## W## D##" (inch)
    packaging_dimensions?: import('../utils/packaging').ProductPackagingDimensions;
}

export interface ApiProductPrintLabel extends ApiPrintLabelBase {
    item_label?: string; // 直播分组名称
}

// No purchase scope: one independent product label per SKU, using its current sale price.
// 传 lineId 时按该采购行的已接单件数出标签：直播不再需要的那几件带空白场次信息，
// 件数在 orphan_count 里，收货时要进备用仓库。
export const getProductPrintLabels = (productMasterId: number, orderId?: number, lineId?: number) => {
    const q = new URLSearchParams({ product_master_id: String(productMasterId) });
    if (orderId != null) q.set('order_id', String(orderId));
    if (lineId != null) q.set('line_id', String(lineId));
    return request<{ labels: ApiProductPrintLabel[]; orphan_count?: number }>(`/product-print-labels?${q.toString()}`);
};

export const getProductsPrintLabels = (targets:{productMasterId:number;orderId?:number;lineId?:number}[]) =>
    request<{items:{labels:ApiProductPrintLabel[];orphan_count?:number}[];packaging_config?:import('../lab/packaging/contract').PackagingConfig}>(`/product-print-labels/batch`,{method:'POST',
        body:JSON.stringify({targets:targets.map(row=>({product_master_id:row.productMasterId,...(row.orderId!=null?{order_id:row.orderId}:{}),...(row.lineId!=null?{line_id:row.lineId}:{})}))})});

// Dispatch fully-rendered label images to 快麦云 raster print (imageWrite).
/** Explicit printer dispatch may queue Catalog Summary; saving a PDF never does. */
export const queueLivestreamSummaryEmail = (livestreamId: number) =>
    request<{summary_email_task?: LivestreamSummaryEmailTaskResult; summary_email_error?: string}>(`/livestreams/${livestreamId}/summary-email`, {method: 'POST'});

export const printLabelImages = (
    images: { image: string; sku: string }[],
    printTimes = 1,
    livestreamId?: number,
    triggerSummaryEmail = false,
) =>
    request<PrintDispatchResult>(`/print-label-images`, {
        method: 'POST',
        body: JSON.stringify({
            images,
            print_times: printTimes,
            livestream_id: livestreamId,
            trigger_summary_email: triggerSummaryEmail,
        }),
    });

/** Field editor acknowledgement; avoids hydrating product, procurement and pricing details. */
export const updateLivestreamItemFields = (
    liveId: number, itemId: number, payload: Partial<Pick<ApiLivestreamItem, 'reserved_quantity' | 'final_listing_price' | 'final_listing_price_currency'>>,
) => request<Pick<ApiLivestreamItem, 'id' | 'reserved_quantity' | 'reserved_fulfilled_quantity' | 'sold_quantity' | 'final_listing_price' | 'final_listing_price_currency'>>(
    `/livestreams/${liveId}/items/${itemId}?response=fields`, { method: 'PATCH', body: JSON.stringify(payload) },
);

export const setLiveSelectionQuantity = (liveId:number, ids:number[], quantity:number) =>
    request<{items:Pick<ApiLivestreamItem,'id'|'reserved_quantity'|'reserved_fulfilled_quantity'>[]}>(`/livestreams/${liveId}/items/quantity`, {method:'PATCH',body:JSON.stringify({ids,quantity})});

export const setPurchaseSelectionQuantity = (orderId:number, ids:number[], quantity:number) =>
    request<{items:Pick<ApiProcurementLine,'id'|'target_quantity'>[]}>(`/procurements/${orderId}/lines/quantity`, {method:'PATCH',body:JSON.stringify({ids,quantity})});

export const deleteLivestreamItem = (liveId: number, itemId: number) =>
    request<{ deleted: boolean; item_id: number; livestream?:ApiBulkRemoveLivestreamItemsResult['livestream'] }>(`/livestreams/${liveId}/items/${itemId}`, { method: 'DELETE' });

// ─── Procurement ──────────────────────────────────────────────────────────────

export type ProcurementStatus = 'selection' | 'in_progress' | 'completed';
export type LineStatus = 'pending_purchase' | 'purchased' | 'receiving' | 'in_transit' | 'completed' | 'exception' | 'out_of_stock';

export interface ApiProcurementOrder {
    picking_views?:Array<'selection'|'purchase'>;
    supplier_collaboration?:SupplierCollaboration|null; picking_suppliers?:Array<{id:number;name:string}>;
    can_print_labels?: boolean;
    printable_line_count?: number;
    can_change_warehouse?: boolean;
    procurement_started?: boolean;
    source_number?: string | null;
    can_delete?: boolean;
    delete_blocked_reason?: string | null;
    id: number;
    order_code: string;
    name: string;
    description: string;
    status: ProcurementStatus;
    source_type?: 'manual' | 'product_import' | string;
    status_label: string;
    suppliers: Array<{ id: number; supplier_id: number; name: string; supplier_name?: string | null; brand_story?: string | null; supplier_brand_story?: string | null }>;
    target_quantity: number;
    target_warehouse_id: number | null;
    target_warehouse_code: string | null;
    target_warehouse_name: string | null;
    // The order's stock lands in this warehouse's inventory location. A livestream
    // can only pull from orders whose location matches its own.
    target_warehouse?: {
        id: number;
        name: string;

        inventory_location_id: number | null;
        inventory_location_code: string | null;
        inventory_location_name: string | null;
    } | null;
    line_count: number;
    total_line_target_quantity?: number;
    received_line_count?: number;
    pending_receipt_line_count?: number;
    pending_receipt_quantity?: number;
    accepted_line_count?: number;
    total_accepted_quantity?: number;
    pending_acceptance_line_count?: number;
    pending_acceptance_quantity?: number;
    total_purchased_quantity: number;
    total_short_quantity?: number;
    total_received_quantity: number;
    number_date: string | null;
    expected_arrival_date?: string | null;
    completed_at: string | null;
    added_to_livestream_id: number | null;
    added_to_livestream_name: string | null;
    livestream_additions?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        quantity_added: number;
    }>;
    livestream_imports?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        processed_sku_count: number;
    }>;
    created_at: string;
    updated_at: string;
    tracking_numbers: ApiPurchaseShipment[];
}

export type AiPlanningReadiness = 'low' | 'medium' | 'high';

export interface ApiAiPlanningSource { id: number; name: string | null; order_code: string }

export interface ApiAiPlanningProject {
    id: number;
    name: string;
    status: string;
    location_id: number;
    location: ApiInventoryLocationInfo | null;
    procurement_order_ids: number[];
    procurement_orders?: ApiAiPlanningSource[];
    latest_enrichment_job?: ApiProductEnrichmentJob | null;
    created_at: string;
    updated_at: string;
}

export interface ApiAiPlanningCandidate {
    product_master_id: number;
    sku: string;
    readiness: AiPlanningReadiness;
    missing_fields: string[];
    attribute_enrichment_eligible: boolean;
    attribute_status: 'complete' | 'partial' | 'invalid';
    cost_hint: { amount: number; currency: string; source: string } | null;
    product_master: ApiProductSummary;
    source_order_ids: number[];
}

export interface ApiAiPlanningReadinessResult {
    project: ApiAiPlanningProject;
    summary: Record<string, number>;
    items: ApiAiPlanningCandidate[];
    pagination: { total: number; page: number; per_page: number; pages: number };
}

export interface ApiProductEnrichmentItem {
    id: number;
    job_id: number;
    product_master_id: number;
    sku: string | null;
    status: string;
    attempt_count: number;
    attributes_before: ApiProductSummary['attribute_values'];
    generated_attributes: Record<string, unknown>;
    attributes_written: Record<string, string[]>;
    attribute_suggestions: Record<string, string[]>;
    attribute_evidence: Record<string, string>;
    confidence: string | null;
    needs_review: boolean;
    unique_shape_score?: number;
    unique_shape_reasons?: string[];
    short_reason: string | null;
    review_reason: string | null;
    error_message: string | null;
    usage?: Record<string, unknown> | null;
}

export interface ApiProductEnrichmentJob {
    id: number;
    project_id: number | null;
    status: string;
    write_mode: 'replace_empty';
    targets: Array<'attributes' | 'merchandising' | 'pot_visual_language'>;
    model: string;
    prompt_version: string;
    requested_count: number;
    counts: Record<string, number>;
    items?: ApiProductEnrichmentItem[];
    error_message: string | null;
    created_at: string;
    updated_at: string;
}

export const getAiPlanningSources = (locationId?: number, signal?: AbortSignal) =>
    request<{ items: ApiAiPlanningSource[] }>(`/ai-planning/sources?view=choices${locationId ? `&location_id=${locationId}` : ''}`, {signal});

export const getAiPlanningProjects = (page = 1, perPage = 20, signal?:AbortSignal) =>
    request<{ items: ApiAiPlanningProject[]; total: number; page: number; per_page: number; pages: number }>(`/ai-planning/projects?page=${page}&per_page=${perPage}`, {signal});

export interface ApiSupplierSelectionSource { id:number; name:string|null; product_count:number; }
export interface ApiSupplierSelectionSnapshot {
    id:string; supplier_id:number; schema_version:string; product_count:number;
    content_hash:string; created_at?:string; reused?:boolean;
}
export interface ApiSupplierSelectionEnvelope {
    id:string; content_hash:string; created_at:string;
    content:{schema_version:string; supplier:{id:number;name:string|null}; products:ApiSupplierSelectionProduct[];
        units:{dimensions:string;weight:string}; evidence_policy:string};
}
export interface ApiSupplierSelectionProduct {
    catalog:Pick<SupplierProduct,'id'|'supplier_id'|'item_code'|'supplier_sku'|'name'|'price_currency'|
        'height'|'width'|'depth'|'weight'|'product_labels'|
        'product_note'|'attributes'|'front_url'|'bottom_url'|'detail_url'|'revision'|'status'> & {
            price:string|null;ext_length:number|null;ext_width:number|null;ext_height:number|null};
    legacy:{schema:string;product_id:number;provenance:Record<string,unknown>};
    evidence_refs:Array<{id:number;source_schema:string;source_product_id:number;stage:string;source_key:string;content_hash:string}>;
}
export const getSupplierSelectionSources = (signal?:AbortSignal) =>
    request<{items:ApiSupplierSelectionSource[];schema_version:string}>('/ai-planning/supplier-sources',{signal});
export const getSupplierSelectionSnapshots = (supplierId:number,signal?:AbortSignal,fresh=false) =>
    request<{items:ApiSupplierSelectionSnapshot[]}>(`/ai-planning/supplier-sources/${supplierId}/snapshots`,{signal},fresh);
export const createSupplierSelectionSnapshot = (supplierId:number,signal?:AbortSignal) =>
    request<ApiSupplierSelectionSnapshot>(`/ai-planning/supplier-sources/${supplierId}/snapshots`,{method:'POST',signal});
export const getSupplierSelectionSnapshot = (id:string,signal?:AbortSignal) =>
    request<ApiSupplierSelectionEnvelope>(`/ai-planning/supplier-snapshots/${encodeURIComponent(id)}`,{signal});
export const getSupplierSelectionEvidence = (snapshotId:string,artifactId:number,signal?:AbortSignal) =>
    request<{id:number;content_hash:string;content:Record<string,unknown>}>(`/ai-planning/supplier-snapshots/${encodeURIComponent(snapshotId)}/evidence/${artifactId}`,{signal});

export const createAiPlanningProject = (payload: { name?: string; location_id?: number; procurement_order_ids: number[] }) =>
    request<ApiAiPlanningProject>('/ai-planning/projects', { method: 'POST', body: JSON.stringify(payload) });

export const getAiPlanningProject = (projectId: number, signal?:AbortSignal) =>
    request<ApiAiPlanningProject>(`/ai-planning/projects/${projectId}`, {signal});

export const updateAiPlanningProject = (projectId: number, payload: { name: string }) =>
    request<Pick<ApiAiPlanningProject, 'id' | 'name'>>(`/ai-planning/projects/${projectId}?view=fields`, { method: 'PATCH', body: JSON.stringify(payload) });

export const addAiPlanningProjectSources = (projectId: number, procurementOrderIds: number[]) =>
    request<ApiAiPlanningProject>(`/ai-planning/projects/${projectId}/procurement-orders`, {
        method: 'POST', body: JSON.stringify({ procurement_order_ids: procurementOrderIds }),
    });

export const getAiPlanningReadiness = (projectId: number, page = 1, perPage = 60, readiness = '', includeSummary = true, signal?: AbortSignal, q = '') =>
    request<ApiAiPlanningReadinessResult>(`/ai-planning/projects/${projectId}/readiness?page=${page}&per_page=${perPage}&include_summary=${includeSummary ? '1' : '0'}${readiness ? `&readiness=${readiness}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`, {signal});

export const getProductEnrichmentJob = (jobId: number) =>
    request<ApiProductEnrichmentJob>(`/ai-planning/product-enrichment-jobs/${jobId}`);

export const retryProductEnrichmentJob = (jobId: number, itemIds?: number[]) =>
    request<ApiProductEnrichmentJob>(`/ai-planning/product-enrichment-jobs/${jobId}/retry`, {
        method: 'POST', body: JSON.stringify({ item_ids: itemIds ?? [] }),
    });

export interface ApiMerchandisingPlanItem {
    product_master_id?:number;
    sku: string;
    run_order: number;
    group: string;
    price_tier: '+' | '++' | '+++' | null;
    selection_target_tier: '+' | '++' | '+++' | 'average' | null;
    selection_source: 'quota' | 'backfill';
    backfill_for: string | null;
    selling_points: string[];
    reason: string;
    primary_decoration: string;
    needs_review: boolean;
    visual_summary: {
        color: string | null;
        front_profile: string | null;
        opening_shape: string | null;
        subjects: string[];
        pattern: string | null;
    };
}

export interface ApiMerchandisingGroupDiagnostic {
    group: string;
    quota: number;
    raw_candidate_count: number;
    white_filtered_count: number;
    overpriced_filtered_count: number;
    eligible_candidate_count: number;
    candidate_count: number;
    target_tier_counts: Record<string, number>;
    selected_tier_counts: Record<string, number>;
    fallback_count: number;
    fallbacks: Array<{ target_tier: string; actual_tier: string; sku: string }>;
}

export interface ApiMerchandisingPlan {
    items: ApiMerchandisingPlanItem[];
    import_rows: Array<Record<string, string | number | boolean>>;
    selected_count: number;
    quota_selected_count: number;
    backfilled_count: number;
    target_count: number;
    quota_gaps: Array<{ group: string; missing: number }>;
    gaps: Array<{ group: string; missing: number }>;
    group_diagnostics: ApiMerchandisingGroupDiagnostic[];
    manual_groups: Array<{ group: string; count: number }>;
    selection_excluded: Array<{ sku: string; group: string; reasons: string[] }>;
    selection_seed: string | null;
}

export interface ApiMerchandisingPreview {
    strategy_version: string;
    prompt_version: string;
    model: string;
    classification_pipeline: 'visual_facts_v0.4';
    target_visual_language_version?: string | null;
    plan: ApiMerchandisingPlan;
    coverage: ApiMerchandisingCounts;
    plan_ready: boolean;
    review_policy: 'warning_only';
    unique_review: ApiMerchandisingPlanItem[];
    excluded: Array<{ sku: string; group?: string; reasons: string[] }>;
    warnings: string[];
    visual_language_versions: string[];
    derivation_version?: string | null;
    projection_version: string;
    projection_hash: string | null;
    selection_profile_version: string;
    selection_profile_hash: string;
    selection_engine_version: string;
    selection_seed: string | null;
    selection_profile_snapshot: ApiSelectionProfile;
}

export interface ApiSelectionProfile { version: string; description: string; multiplier: number; rounding: 'ceil' | 'exact'; selection_engine_version: string; backfill: { enabled: boolean; tiers: string[]; deduplicate: boolean }; groups: Array<{ name: string; priority: number; quotas: Record<string, number> }> }
export interface ApiSelectionProfileSummary { groups?: Array<{ name: string; quotas: Record<string, number> }>; version: string; description: string; multiplier: number; rounding: 'ceil' | 'exact'; selection_engine_version: string; backfill_enabled: boolean; hash: string }

export interface ApiLivePlanDraft {
    plan_id: string;
    project_id: number;
    project_location_id: number;
    content_hash: string;
    input_mode: 'planning_brief' | 'legacy_presenter_copy' | 'template';
    original_input: { planning_brief: string } | { description: string; product_pitch_script: string } | { selection_mode: 'template'; profile_version: string; procurement_order_ids: number[]; sales_theme_enabled: boolean; sales_theme_count: number };
    planning_input_hash: string;
    application_token: string;
    application_token_expires_in: number;
    interpreted_intent: string;
    assumptions: string[];
    model: string;
    prompt_version: string;
    result: ApiMerchandisingPreview & {
        interpreted_intent: string;
        assumptions: string[];
        sales_theme_profile_snapshot?: ApiSelectionProfile | null;
        plan: ApiMerchandisingPreview['plan'] & {
            groups: Array<{ name: string; description: string; target_count: number; mandatory_review: boolean }>;
        };
    };
}

export interface ApiMerchandisingCounts {
    total_eligible: number;
    classified: number;
    queued: number;
    processing: number;
    flagged: number;
    failed: number;
    unknown: number;
    remaining: number;
    coverage: number;
}

export const getSelectionProfiles = (signal?: AbortSignal) => request<{ default_version: string; items: ApiSelectionProfileSummary[] }>('/ai-planning/selection-profiles', {signal});

export interface ApiPotKnowledgeVersion {
    version: string;
    title?: string;
    status: 'frozen' | 'invalid';
    valid: boolean;
    bundle_sha256?: string;
    knowledge_version?: string;
    prompt_version?: string;
    derivation_version?: string;
    errors: string[];
}

export interface ApiPotKnowledgeBundle {
    manifest: ApiPotKnowledgeVersion & Record<string, unknown>;
    schema: { fields: Array<{
        key: string;
        type: string;
        multi_value: boolean;
        applicability?: string;
        labels?: Record<string, string>;
        definitions?: Record<string, string>;
        track?: 'A' | 'B';
        measurement_status?: 'model_estimated' | 'unavailable' | 'not_applicable';
    }> };
    vocabulary: { values: Record<string, Array<string | { key: string; labels?: Record<string, string> }>> };
    ontology: { relationships: unknown[] };
    response_schema: Record<string, unknown>;
    prompt: string;
    derivation_rules: Record<string, unknown>;
    projection?: {
        version: string;
        derivation_version: string;
        rules: Array<{ id: string; output_flag: string; condition: Record<string, unknown>; evidence_dependencies: string[]; unknown_behavior: string; explanation: string }>;
    };
}

export interface ApiPotClassificationPreview {
    project_id: number;
    bundle_version: string;
    bundle_sha256: string;
    model: string;
    reasoning_effort: string;
    max_cost_usd: number;
    configured_max_cost_usd: number | null;
    confirmation_hash: string;
    sku_count: number;
    image_count: number;
    estimated_cost_usd: number;
    raw_po_line_count: number; unique_product_master_count: number;
    reused_classification_count: number; new_classification_count: number;
    skipped_product_count: number; conflicted_product_count: number;
    unpreviewed_product_count: number;
    identity_conflicts: Array<Record<string, unknown>>;
    skipped_products: Array<Record<string, unknown>>;
    pricing: Record<string, unknown>;
    automatic_max_cost_usd: number | null;
    jobs_count: number;
    chunk_size: number;
    chunks: Array<{ index: number; sku_count: number; estimated_cost_usd: number; max_cost_usd: number }>;
    mode: 'selected' | 'trial' | 'batch' | 'all' | 'upgrade' | 'retry_failed' | 'rerun';
    eligible_count?: number;
    active_skipped_count?: number;
    remaining_total: number | null;
    remaining_after_batch: number | null;
    items?: Array<{ product_master_id: number; sku: string; images: Array<{ asset_id: number | null; role: string; url: string }>; estimated_cost_usd: number }>;
    prompt: string;
    schema: ApiPotKnowledgeBundle['schema'];
    response_schema: Record<string, unknown>;
    derivation_rules: Record<string, unknown>;
}

export interface ApiPotClassificationBatchResult {
    jobs_created: number;
    job_ids: number[];
    requested_count: number;
    chunk_size: number;
    estimated_cost_usd: number;
    max_cost_usd: number;
    jobs: ApiProductEnrichmentJob[];
}

export interface ApiPotObservation {
    field: string; scope_id: string;
    state: 'value' | 'unknown' | 'not_visible' | 'not_applicable' | 'out_of_vocabulary';
    value: string | Record<string, unknown> | null;
    image_ids: string[]; evidence: string;
    confidence_band: 'high' | 'medium' | 'low';
    review_status: 'pending' | 'reviewed' | 'conflict';
    schema_version: string; candidate_description: string | null;
    unavailable_children: Array<{ path: string; reason: string }>;
}
export interface ApiPotFact {
    state: string; values: string[]; confidence: number; evidence: string;
    observations?: ApiPotObservation[];
    unavailable_reason?: string | null;
}
export interface ApiPotClassificationItem {
    id: number;
    job_id: number;
    product_master_id: number | null;
    supplier_product_id?: number | null;
    history_read_only?: boolean;
    sku: string;
    status: string;
    attempt_count: number;
    images: Array<{ asset_id: number | null; role: string; url: string; content_sha256?: string }>;
    schema_version?: string;
    provenance?: Record<string, unknown>;
    instances?: Array<Record<string, unknown>>;
    features?: Array<Record<string, unknown>>;
    measurements?: Array<Record<string, unknown>>;
    color_regions?: Array<Record<string, unknown>>;
    model_facts: Record<string, ApiPotFact>;
    model_derived_flags: Record<string, boolean | null>;
    effective_facts: Record<string, ApiPotFact>;
    effective_derived_flags: Record<string, boolean | null>;
    derivation_version?: string | null;
    projection_version?: string | null;
    visual_description: string;
    review_diagnostics?: {
        reason_codes?: string[]; conflicting_fields?: string[];
        planning_relevant_missing_fields?: string[]; evidence_insufficient_count?: number;
        oov_candidates?: Array<Record<string, unknown>>; quarantine_count?: number;
        semantic_conflicts?: string[];
        data_quality_issues?: Array<{ field?: string; category: 'scope_mismatch' | 'image_scope_mismatch' | 'invalid_payload'; scope_id?: string; image_ids?: string[] }>;
        evidence_insufficient?: Array<{ field: string; reason?: string | null }>;
        out_of_vocabulary?: Array<Record<string, unknown>>;
    };
    correction_revision: number;
    correction_events: Array<Record<string, unknown>>;
    evidence: { quarantine?: Array<Record<string, unknown>>; raw_response?: unknown };
    confidence: string | null;
    needs_review: boolean;
    review_reason: string | null;
    error_message: string | null;
    usage?: Record<string, unknown>;
}

export interface ApiPotClassificationSummary {
    id: number;
    job_id: number;
    product_master_id: number | null;
    supplier_product_id?: number | null;
    history_read_only?: boolean;
    sku: string;
    status: string;
    attempt_count: number;
    schema_version: string;
    confidence: string | null;
    correction_revision: number;
    effective_derived_flags: Record<string, boolean | null>;
    error_message: string | null;
    image_asset_ids: number[];
    projection_version?: string | null;
}

export const getPotKnowledgeVersions = () =>
    request<{ items: ApiPotKnowledgeVersion[]; default_version: string }>('/ai-planning/pot-visual-language/versions');

export const getPotKnowledgeVersion = (version: string, signal?:AbortSignal) =>
    request<ApiPotKnowledgeBundle>(`/ai-planning/pot-visual-language/versions/${encodeURIComponent(version)}`,{signal});

export interface ApiPotDerivationVersion {
    version: string; derivation_version: string; compatible_bundle: string; hash: string; rule_count: number;
}

export const getPotDerivationVersions = (compatibleBundle: string) =>
    request<{ default_version: string | null; items: ApiPotDerivationVersion[] }>(`/ai-planning/pot-derivation-versions?compatible_bundle=${encodeURIComponent(compatibleBundle)}`);

export const previewPotClassificationJob = (projectId: number, payload: Record<string, unknown>) =>
    request<ApiPotClassificationPreview>(`/ai-planning/projects/${projectId}/pot-classification-jobs/preview`, { method: 'POST', body: JSON.stringify(payload) });

export const createPotClassificationJob = (projectId: number, payload: Record<string, unknown>) =>
    request<ApiProductEnrichmentJob | ApiPotClassificationBatchResult>(`/ai-planning/projects/${projectId}/pot-classification-jobs`, { method: 'POST', body: JSON.stringify(payload) });

export const getPotClassificationJobs = (projectId: number) =>
    request<{ items: ApiProductEnrichmentJob[] }>(`/ai-planning/projects/${projectId}/pot-classification-jobs`);

export interface ApiPotClassificationCoverage {
    target_version: string; target_bundle_sha256: string; total_eligible: number;
    successful: number; flagged: number; queued: number; processing: number;
    failed: number; outdated: number; unclassified: number; ready_for_final_plan: boolean;
}

export interface ApiPotSubmissionProgress {
    confirmation_hash: string; version: string; submission_mode: string | null;
    total: number; completed: number; flagged: number; failed: number; queued: number; processing: number;
    percentage: number; created_at: string | null; updated_at: string | null;
    current_item: { item_id: number; job_id: number; product_master_id: number; sku: string; processing_started_at: string | null } | null;
    jobs: Array<Pick<ApiProductEnrichmentJob, 'id' | 'status' | 'requested_count' | 'counts' | 'created_at' | 'updated_at'>>;
}

export const getPotClassificationProgress = (projectId: number, signal?: AbortSignal) =>
    request<{ items: ApiPotSubmissionProgress[] }>(`/ai-planning/projects/${projectId}/pot-classification-progress`, { signal });

export const getPotClassificationCoverage = (projectId: number, version: string, signal?: AbortSignal) =>
    request<ApiPotClassificationCoverage>(`/ai-planning/projects/${projectId}/pot-classification-coverage?version=${encodeURIComponent(version)}`, { signal });

export const getPotProjectClassificationItems = (projectId: number, params: { version: string; page?: number; per_page?: number; status?: string; sku?: string; projection_version?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value !== undefined && value !== '' && query.set(key, String(value)));
    return request<{ items: ApiPotClassificationSummary[]; pagination: { total: number; page: number; per_page: number; pages: number } }>(`/ai-planning/projects/${projectId}/pot-classification-results?${query}`, { signal });
};

export const getPotProjectClassificationItemDetail = (projectId: number, itemId: number, params: { version: string; projection_version?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value !== undefined && value !== '' && query.set(key, String(value)));
    return request<ApiPotClassificationItem>(`/ai-planning/projects/${projectId}/pot-classification-results/${itemId}?${query}`, { signal });
};

export interface ApiPotDiscoveryReportSummary {
    submission_hash: string; submission_mode: string; bundle_version: string; created_at: string;
    candidate_group_count: number; affected_sku_count: number; job_ids: number[];
    synthesis: { status: string; usage?: { cost_usd?: number }; estimated_cost_usd?: number; error?: string };
}
export interface ApiPotDiscoveryReport extends ApiPotDiscoveryReportSummary {
    candidate_snapshot: Array<Record<string, unknown>>;
    synthesis_input?: Record<string, unknown>;
    synthesis: ApiPotDiscoveryReportSummary['synthesis'] & { recommendations?: Array<{
        action: string; field: string; candidate: string; proposed_value: string | null;
        reason: string; confidence: number; supporting_skus: string[]; supporting_sku_count: number;
    }> };
}
export const getPotDiscoveryStages = (projectId: number, signal?: AbortSignal) =>
    request<{ items: ApiPotDiscoveryReportSummary[]; available: boolean; reason?: string }>(`/ai-planning/projects/${projectId}/pot-discovery-stages`, { signal });
export const getPotDiscoveryStage = (projectId: number, submissionHash: string) =>
    request<ApiPotDiscoveryReport>(`/ai-planning/projects/${projectId}/pot-discovery-stages/${submissionHash}`);
export const potDiscoveryStageDownloadUrl = (projectId: number, submissionHash: string) =>
    `${BASE}/ai-planning/projects/${projectId}/pot-discovery-stages/${submissionHash}?download=true`;

export const correctPotClassificationItem = (itemId: number, payload: Record<string, unknown>) =>
    request<ApiPotClassificationItem>(`/ai-planning/pot-classification-items/${itemId}/corrections`, { method: 'POST', body: JSON.stringify(payload) });

export const potClassificationExportUrl = (jobId: number, format: 'json' | 'csv') =>
    `${BASE}/ai-planning/pot-classification-jobs/${jobId}/export?format=${format}`;

export interface ApiPurchaseShipment {
    id?: string;
    no: string;
    company: string;
    line_ids?: number[];
    shipped_at?: string | null;
    recipient_name?: string | null;
    recipient_phone?: string | null;
    ship_to_address?: string | null;
    items?: Array<{line_id:number; product_master_id:number; name?:string; sku?:string; quantity?:number}>;
}
export type ApiShipmentConfirmation = {id:number; status:ApiProcurementOrder['status']; tracking_numbers:ApiPurchaseShipment[]};
export const registerPurchaseShipment = (id:number, data:{tracking_number:string; shipping_carrier:string; items:Array<{line_id:number;quantity:number}>}) =>
    request<ApiShipmentConfirmation>(`/procurements/${id}/shipments`,{method:'POST',body:JSON.stringify(data)});
export const correctPurchaseShipments = (id:number, shipments:Array<{id:string;tracking_number:string;shipping_carrier:string;previous_number:string;previous_company:string}>) =>
    request<ApiShipmentConfirmation>(`/procurements/${id}/shipments`,{method:'PATCH',body:JSON.stringify({shipments})});
export const correctPurchaseShipment = (id:number, shipment:ApiPurchaseShipment, data:{tracking_number:string;shipping_carrier:string}) =>
    request<ApiShipmentConfirmation>(`/procurements/${id}/shipments/${encodeURIComponent(shipment.id!)}`,{method:'PATCH',body:JSON.stringify({...data,previous_number:shipment.no,previous_company:shipment.company})});

export interface ApiProcurementList {
    items: ApiProcurementOrder[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export type ApiProcurementSummary = Pick<ApiProcurementOrder, 'supplier_collaboration' | 'picking_suppliers' | 'pending_receipt_line_count' | 'pending_receipt_quantity' | 'accepted_line_count' | 'total_accepted_quantity' | 'pending_acceptance_line_count' | 'pending_acceptance_quantity' | 'received_line_count' | 'id' | 'order_code' | 'name' | 'description' | 'status' | 'created_at' | 'number_date' | 'target_warehouse_id' | 'target_warehouse_name' | 'line_count' | 'total_purchased_quantity' | 'total_received_quantity' | 'total_short_quantity' | 'can_delete' | 'procurement_started' | 'source_type' | 'source_number' | 'total_line_target_quantity'> & { status_label?:string; source_series_type?: 'live' | 'purge' | null; source_title?:string|null; cover_url?:string|null; suppliers: Array<{ id: number; supplier_id: number; name: string }> };
export const getProcurementSummaries = (params?: {
    type?: 'live' | 'regular';
    series_type?: 'live' | 'purge';
    receiving_only?: boolean;
    month?: string;
    status?: string;
    statuses?: ProcurementStatus[];
    warehouse_id?: number | string;
    warehouse_code?: string;
    q?: string;
    page?: number;
    per_page?: number;
    sort?: string;
}, opts?: { signal?: AbortSignal }) => {
    const q = new URLSearchParams({ view: 'summary' });
    if (params?.type) q.set('type', params.type);
    if (params?.series_type) q.set('series_type', params.series_type);
    if (params?.receiving_only) q.set('receiving_only', 'true');
    if (params?.month) q.set('month', params.month);
    if (params?.status) q.set('status', params.status);
    params?.statuses?.forEach(status => q.append('statuses', status));
    if (params?.warehouse_id) q.set('warehouse_id', String(params.warehouse_id));
    if (params?.warehouse_code) q.set('warehouse_code', params.warehouse_code);
    if (params?.q) q.set('q', params.q);
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.sort) q.set('sort', params.sort);
    const qs = q.toString();
    return request<Omit<ApiProcurementList, 'items'> & { items: ApiProcurementSummary[]; warehouses: Array<{ id: number; name: string; code?: string; inventory_location_code?: string | null }> }>(`/procurements${qs ? `?${qs}` : ''}`, { signal: opts?.signal });
};

export const duplicateProcurement = (id: number) =>
    request<Pick<ApiProcurementSummary,'id'|'order_code'|'name'>>(`/procurements/${id}/duplicate`, { method: 'POST' });

export const deleteProcurement = (id: number) =>
    request<{ message: string }>(`/procurements/${id}`, { method: 'DELETE' });

export const updateProcurement = (id: number, payload: Partial<{
    name: string;
    description: string;
    target_quantity: number;
    target_warehouse_id: number;
    number_date: string;
    expected_arrival_date: string | null;
    tracking_numbers: { no: string; company: string; line_ids?:number[] }[];
    supplier_ids: number[];
}>) => request<ApiProcurementOrder>(`/procurements/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

/** Metadata-only writes keep purchase/stock operations on their existing contracts. */
export type ApiProcurementFields = {id: number} & Partial<Pick<ApiProcurementOrder, 'id' | 'name' | 'description' | 'status' | 'target_warehouse_id' | 'target_warehouse' | 'target_warehouse_name' | 'target_warehouse_code' | 'tracking_numbers' | 'number_date' | 'expected_arrival_date' | 'can_change_warehouse' | 'procurement_started'>>;
export const updateProcurementFields = (id: number, payload: Parameters<typeof updateProcurement>[1]) =>
    request<ApiProcurementFields>(`/procurements/${id}?view=fields`, {method:'PATCH', body:JSON.stringify(payload)});
export const getProcurementHeader = (id: number, signal?: AbortSignal) =>
    request<Pick<ApiProcurementOrder, 'id' | 'name' | 'order_code' | 'status' | 'source_type' | 'can_print_labels' | 'target_warehouse_name'>>(`/procurements/${id}?view=header`, {signal});

export type ApiProcurementLineConfirmation = Pick<ApiProcurementLine, 'id'|'product_master_id'|'target_quantity'|'purchased_quantity'|'received_quantity'|'short_quantity'|'purchase_price'|'purchase_currency'|'procurement_status'|'supplier_id'|'notes'> & Partial<ApiProcurementLine> & {order_status?: ProcurementStatus};

export const confirmSalesPurchaseChange=(orderId:number,lineId:number,quantity:number)=>request<{id:number;confirmed_quantity:number}>(`/procurements/${orderId}/lines/${lineId}/confirm-sales-change`,{method:'POST',body:JSON.stringify({quantity})});

export interface ApiProcurementLine extends Partial<ApiProductPosterState> {
    /** Accepted units not yet stocked at the destination warehouse. */
    pending_stock_quantity?: number;
    sequence_number?:number;
    procurement_order_id?:number;
    sales_requested_quantity?:number;
    sales_change_pending?:boolean;
    missing_purchase_fields?:string[];
    shipping_contact?: {recipient_name?:string|null;recipient_phone?:string|null;ship_to_address?:string|null};
    can_remove?:boolean;can_move?:boolean;can_rename?:boolean;
    pricing?: ProductPricing;
    id: number;
    order_id: number;
    line_type: string;
    product_master_id: number | null;
    sku: string | null;
    target_quantity: number;
    // Minimum 目标数 demanded by confirmed-预排 livestreams (increase-only floor).
    livestream_demand_quantity?: number;
    preplan_sequence_number?: number | null;
    preplan_sequence_numbers?: Array<{
        livestream_id: number;
        livestream_name: string | null;
        livestream_code: string | null;
        sequence_number: number;
        sort_order: number;
    }>;
    purchased_quantity: number;
    received_quantity: number;
    /** 按实收完结: ordered but will not arrive, recorded as 缺货. */
    short_quantity?: number;
    is_accepted?: boolean;
    accepted_quantity?: number;
    purchase_price: number | null;
    purchase_currency: string | null;
    status: LineStatus;
    procurement_status: LineStatus;
    status_label: string;
    supplier_id: number | null;
    supplier_name: string | null;
    supplier_sku: string | null;
    // Free-form line note. Used for the 异常说明 (exception explanation).
    notes: string | null;
    original_url: string | null;
    latest_poster_status: CoverPosterJobStatus | null;
    product_master?: ApiProductSummary;
    created_at: string;
    updated_at: string;
}

export interface ApiProductMasterUpdate {
    success: boolean;
    data: Partial<ApiProductSummary> & { master_id: number; updated_fields: string[]; repriced_procurement_lines: number };
}

export interface ApiProductMasterPhotoReplace {
    success: boolean;
    data: {
        id: number;
        sku: string;
        source_original_url: string | null;
        original_url: string | null;
        original_image_status: string | null;
        source_image_kind: ApiSourceImageKind | null;
    };
}
export interface ApiProcurementDetail extends ApiProcurementOrder {
    product_index?: [number,number][];
    order_revision?:number;order_edit_revision?:number;source_order_linked?:boolean;
    out_of_stock_line_count?:number;
    product_tab_counts?: {pending:number;accepted:number;completed:number};
    source_live?: {id:number; title:string|null; name:string|null; series_type:'live'|'purge'; series:LivestreamSeries|null;
        warehouse_name?:string|null; scheduled_at?:string|null} | null;
    view?: 'screen'|'information'|'statistics'|'export'|'print';
    total_line_target_quantity?: number;
    view_settings?: Record<string,string>;
    groups?: Array<{id:number|null;name:string|null;count:number;quantity:number;positions:number[]}>;
    status_groups?: Array<{status:LineStatus;count:number;quantity:number;positions:number[];suppliers:NonNullable<ApiProcurementDetail['groups']>}>;
    distribution?: {totalSkus:number;totalQuantity:number;size:Array<{quantity:number;skus:number}>;price:Array<{quantity:number;skus:number}>};
    lines: ApiProcurementLine[];
    line_pagination?: { total: number; page: number; per_page: number; pages: number };
}

interface ProcurementReadParams {
    lineIds?: number[];
    view?: 'screen'|'information'|'statistics'|'export'|'print';
    viewOptions?: Record<string,string>;
    groupBySupplier?: boolean;
    page?: number;
    perPage?: number;
    q?: string;
    status?: string;
    supplierId?: number | string;
    sort?: string;
}

const procurementReadPath = (id: number, params?: ProcurementReadParams) => {
    const query = new URLSearchParams();
    if(params?.view) query.set('view',params.view);
    if(params?.lineIds) query.set('line_ids',params.lineIds.join(','));
    for(const [key,value] of Object.entries(params?.viewOptions ?? {})) {const name=({supplier:'supplier_id',groupBy:'group_by',sort:'sort',status:'status',productStatus:'product_status'} as Record<string,string>)[key];if(name)query.set(name,value);}
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.q) query.set('q', params.q);
    if (params?.status) query.set('status', params.status);
    if (params?.supplierId) query.set('supplier_id', String(params.supplierId));
    if (params?.groupBySupplier) query.set('group_by', 'supplier');
    if (params?.sort) query.set('sort', params.sort);
    Object.entries(productSortRates(query.get('sort')??'')).forEach(([key,value])=>query.set(key,value));
    // Information/statistics ignore list filters; share their one canonical read.
    if(params?.view === 'information' || params?.view === 'statistics') {
        return `/procurements/${id}?view=${params.view}`;
    }
    query.sort();
    const qs = query.toString();
    return `/procurements/${id}${qs ? `?${qs}` : ''}`;
};

/** Same initial projection used by the drawer and adjacent-record warming. */
export function purchaseScreenOptions(id:number) {
    const saved=readListSettings(`live-view:purchase:${id}`);
    return {sort:typeof saved.sort==='string'?saved.sort:'status:asc',groupBy:'',status:'',supplier:'',productStatus:typeof saved.productStatus==='string'?saved.productStatus:''};
}

export const getProcurement = (id: number, params?: ProcurementReadParams, options?: { signal?: AbortSignal }) =>
    request<ApiProcurementDetail>(procurementReadPath(id, params), options);

export type PurchaseQueueCriteria={order_id?:string;supplier_id?:string;sort?:string;group_by?:string};
export interface ApiPendingPurchaseLines {
    orders?:Array<{id:number;name:string}>;
    suppliers?:Array<{id:number|null;name:string|null}>;
    items: (ApiProcurementLine & {picking_views?:Array<'selection'|'purchase'>;supplier_collaboration?:SupplierCollaboration|null; picking_suppliers?:Array<{id:number;name:string}>;order_source_type?:string;can_print_labels?:boolean;receipt_context?:{
        order_name:string|null;order_code:string;warehouse_name:string|null;supplier_name:string|null;purchased_quantity:number;
        product:Pick<ApiProductSummary,'name'|'height'|'width'|'depth'|'internal_height'|'internal_width'|'internal_depth'|'unit'|'brand'>;
    }})[];
    total: number;
    pages: number;
    groups: Array<{id:number|null;name:string|null;count:number;quantity:number;start:number}>;
}
export const getPendingPurchaseLines = (page:number, q:string, signal?:AbortSignal, criteria:PurchaseQueueCriteria={}) =>
    request<ApiPendingPurchaseLines>(`/procurements/pending-lines?${new URLSearchParams({page:String(page),per_page:'20',q,...criteria,...productSortRates(criteria.sort)})}`, {signal});
export const getPendingReceiptLines = (page:number, q:string, productIds:number[]|null, orderId?:number, signal?:AbortSignal, criteria:PurchaseQueueCriteria & {include_arrivals?:'1'}={}) => {
    const params=new URLSearchParams({page:String(page),per_page:'20',q,...criteria,...productSortRates(criteria.sort)});
    if(productIds!==null)params.set('product_ids',productIds.join(','));
    if(orderId!==undefined)params.set('order_id',String(orderId));
    return request<ApiPendingPurchaseLines>(`/procurements/pending-receipt-lines?${params}`,{signal});
};
export const identifyPendingReceipts = (photo:File) =>
    postProcurementPhoto<{product_ids:number[]}>('/procurements/identify-pending-receipts',photo);

export const getPurchaseWorkCounts = (signal?:AbortSignal) =>
    request<{pending_purchase:number;pending_receipt:number}>('/procurements/work-counts', {signal});





export type ProductBatchDestination = 'live_unpurchased' | 'live_purchased' | 'procurement' | 'procurement_purchased' | 'pool' | 'order' | 'order_draft' | 'live_library' | 'procurement_library';

export interface ApiProductBatchRow {
    id?:number;
    sheet_name?: string;
    sheet_row?: number;
    requested_quantity?: number | null;
    skip_reason?: string | null;
    correction_fields?:{key:string;label:string;value:string;message:string}[];
    row_number: number;
    name: string | null;
    supplier_name: string | null;
    supplier_sku: string | null;
    quantity: number | null;
    /** preview: ok / error; after import also created. */
    status: 'ok' | 'error' | 'created' | 'skipped';
    errors: string[];
    sku?: string | null;
}

export interface ApiProductBatchPreview {
    destination: ProductBatchDestination;
    row_count: number;
    ok_count: number;
    rows: ApiProductBatchRow[];
}

export interface ApiProductBatchResult {
    destination: ProductBatchDestination;
    row_count: number;
    created_count: number;
    skipped_count?: number;
    rows: ApiProductBatchRow[];
}

export const getProductBatchTargets = (destination:ProductBatchDestination|'stock_update',q='',signal?:AbortSignal,targetId?:number) =>
    request<{id:number;name:string|null}[]>(`/product-batch/targets?${new URLSearchParams({destination,q,...(targetId?{target_id:String(targetId)}:{})})}`,{signal});
export async function downloadProductBatchTemplate(destination: ProductBatchDestination = 'procurement'): Promise<void> {
    const res = await erpFetch(`${BASE}/product-batch/template?destination=${destination}`);
    if (!res.ok) throw await apiError(res);
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = destination.endsWith('_library') ? 'ERP_已有花盆选择模板.xlsx' : 'ERP_新花盆选入模板.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function productBatchForm(destination: ProductBatchDestination, targetId: number, file: File, defaultSupplierId?: number | null, corrections?:ImportCorrections, rowNumber?:number) {
    const form = new FormData();
    form.append('destination', destination);
    form.append('target_id', String(targetId));
    form.append('file', file);
    if (defaultSupplierId) form.append('default_supplier_id', String(defaultSupplierId));
    if(corrections)form.append('corrections',JSON.stringify(corrections));
    if(rowNumber!==undefined)form.append('row_number',String(rowNumber));
    return form;
}

export async function previewProductBatch(
    destination: ProductBatchDestination, targetId: number, file: File, defaultSupplierId?: number | null, corrections?:ImportCorrections, rowNumber?:number,signal?:AbortSignal,
): Promise<ApiProductBatchPreview> {
    const res = await erpFetch(`${BASE}/product-batch/preview`, {
        method: 'POST', signal,body: productBatchForm(destination, targetId, file, defaultSupplierId, corrections, rowNumber),
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

/** Records the rows; streams progress (done / total) and resolves with every row's outcome. */
type ProductBatchOptions={signal?:AbortSignal;defaultSupplierId?:number|null;onlyRows?:number[];requestKey?:string;corrections?:ImportCorrections;rowNumber?:number};
export async function importProductBatch(
    destination: ProductBatchDestination, targetId: number, file: File,
    options: ProductBatchOptions = {},
    onProgress?: (done: number, total: number) => void,
): Promise<ApiProductBatchResult> {
    // Catalog rows are independent. Transactional destinations retain their
    // existing completion boundary (e.g. a purchased order closes only once).
    if(destination==='pool' && options.onlyRows && options.onlyRows.length>20){
        const rows:ApiProductBatchRow[]=[];
        const combined=()=>({destination,row_count:rows.length,rows,created_count:rows.filter(row=>row.status==='created').length,skipped_count:rows.filter(row=>row.status==='skipped').length});
        try{
            for(let offset=0;offset<options.onlyRows.length;offset+=20){
                if(options.signal?.aborted)throw new DOMException('Cancelled','AbortError');
                const result=await importProductBatchWindow(destination,targetId,file,{...options,onlyRows:options.onlyRows.slice(offset,offset+20)},
                    done=>onProgress?.(offset+done,options.onlyRows!.length));
                rows.push(...result.rows);
            }
        }finally{
            if(rows.length||options.signal?.aborted)commitStreamChange(`${BASE}/product-batch/import`,combined());
        }
        return combined();
    }
    try{const result=await importProductBatchWindow(destination,targetId,file,options,onProgress);
        const targetKind=destination.startsWith('live')?'live':destination.startsWith('procurement')?'purchase':destination;
        commitStreamChange(`${BASE}/product-batch/import`,{...result,target_kind:targetKind,target_id:targetId,added_product_ids:result.rows.flatMap(row=>row.status==='created'&&row.id?[row.id]:[])});return result;
    }catch(error){if(options.signal?.aborted)commitStreamChange(`${BASE}/product-batch/import`,{destination});throw error;}
}

async function importProductBatchWindow(destination:ProductBatchDestination,targetId:number,file:File,options:ProductBatchOptions,
    onProgress?: (done:number,total:number)=>void):Promise<ApiProductBatchResult> {
    const form = productBatchForm(destination, targetId, file, options.defaultSupplierId, options.corrections, options.rowNumber);
    if (options.onlyRows?.length) form.append('only_rows', JSON.stringify(options.onlyRows));
    if (options.requestKey) form.append('request_key', options.requestKey);
    const res = await erpFetch(`${BASE}/product-batch/import`, { method: 'POST', body: form,signal:options.signal });
    if (!res.ok || !res.body) throw await apiError(res);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: ApiProductBatchResult | null = null;
    const handleLine = (line: string) => {
        if (!line.trim()) return;
        const evt = JSON.parse(line) as
            | { type: 'start'; total: number }
            | { type: 'progress'; done: number; total: number }
            | { type: 'done'; result: ApiProductBatchResult };
        if (evt.type === 'start') onProgress?.(0, evt.total);
        else if (evt.type === 'progress') onProgress?.(evt.done, evt.total);
        else result = evt.result;
    };
    try{
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n')) >= 0) {
            handleLine(buffer.slice(0, idx));
            buffer = buffer.slice(idx + 1);
        }
    }
    }finally{if(!result)await reader.cancel().catch(()=>{});reader.releaseLock();}
    if (buffer.trim()) handleLine(buffer);
    if (!result) throw new Error('导入未完成，请重试');
    return result;
}

export interface ApiImageMatchCandidate extends ApiProductTitle {
    rank: number;
    embedding_id?: number;
    product_master_id: number;
    sku: string;
    display_name?: string | null;
    product_name?: string | null;
    image_url?: string | null;
    product_original_url?: string | null;
    distance: number | null;
    match_bucket: 'strong' | 'possible' | 'weak' | string;
    inventory_summary?: {
        on_hand_quantity: number;
        incoming_quantity: number;
        available_quantity: number;
    };
}

export interface ApiImageMatchAttempt {
    id: number;
    procurement_order_id: number;
    order_code?: string | null;
    procurement_line_id?: number | null;
    uploaded_image_url: string;
    model_name: string;
    model_version: string;
    query_embedding_checksum?: string | null;
    candidate_snapshot?: unknown;
    best_product_master_id?: number | null;
    best_distance?: number | null;
    decision: string;
    selected_product_master_id?: number | null;
    resolved_line_id?: number | null;
    reviewer_id?: number | null;
    resolved_at?: string | null;
    error_message?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface ApiReceiveImageVerification {
    result: 'confirmed' | 'mismatch' | 'uncertain' | 'not_found' | string;
    decision: string;
    allow_receive: boolean;
    manual_review_required: boolean;
    reason: string;
    expected_product_master_id: number | null;
    expected_sku: string | null;
    expected_rank: number | null;
    expected_distance: number | null;
    expected_match_bucket: string | null;
    top_product_master_id: number | null;
    top_sku: string | null;
    top_distance: number | null;
    top_match_bucket: string | null;
}

export interface ApiReceiveImageMatchResponse {
    attempt: ApiImageMatchAttempt;
    verification: ApiReceiveImageVerification;
    candidates: ApiImageMatchCandidate[];
}

async function postProcurementPhoto<T>(path: string, photo: File, fields?: Record<string, string | number | undefined | null>): Promise<T> {
    const form = new FormData();
    form.append('photo', photo);
    Object.entries(fields ?? {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            form.append(key, String(value));
        }
    });

    const res = await erpFetch(`${BASE}${path}`, {
        method: 'POST',
        body: await directMediaForm(`${BASE}${path}`,form),
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
}

const productEditContext = (lineId?: number) => lineId == null ? '' : `?procurement_line_id=${lineId}`;

/** 共用品牌字典（与供应商货盘同一张表）。 */
export interface ApiProductBrand { id: number; name: string }
export const getProductBrands = (signal?:AbortSignal) => request<ApiProductBrand[]>('/product-brands',{signal});

export const updateProductMaster = (masterId: number, payload: {
    name?: string | null;
    height?: number | null;
    width?: number | null;
    depth?: number | null;
    internal_height?: number | null;
    internal_width?: number | null;
    internal_depth?: number | null;
    weight?: number | null;
    unit?: string;
    purchase_url?: string | null;
    opening_diameter?: number | null;
    attribute_changes?: Record<string,string|string[]|null>;
    attribute_ids?: number[];
    attribute_option_ids?: number[];
    product_labels?: string[];
    product_note?: string | null;
    /** 品牌名称：没有就新建一条共用品牌；null 清空。 */
    brand?: string | null;
    supplier_id?: number | null;
    supplier_sku?: string | null;
}, procurementLineId?: number): Promise<ApiProductMasterUpdate> => {
    const url = `${ECOMMERCE_API_BASE_URL}/product/masters/${masterId}${productEditContext(procurementLineId)}`;
    return erpFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).then(async r => {
        if (!r.ok) throw await apiError(r);
        return r.json();
    });
};

export const replaceProductMasterPhoto = async (masterId: number, photo: File, procurementLineId?: number, saveKey?: string): Promise<ApiProductMasterPhotoReplace> => {
    const form = new FormData();
    form.append('photo', photo);
    return erpFetch(`${PRODUCT_BASE}/masters/${masterId}/replace-photo${productEditContext(procurementLineId)}`, {
        method: 'POST',
        headers: productSaveHeaders(saveKey),
        body: await directMediaForm(`${PRODUCT_BASE}/masters/${masterId}/replace-photo${productEditContext(procurementLineId)}`,form),
    }).then(async r => {
        if (!r.ok) throw await apiError(r);
        return r.json();
    });
};

export async function addProductMasterImageAsset(
    masterId: number,
    options: { role: 'bottom' | 'other'; photo?: File; sortOrder?: number },
    procurementLineId?: number,
    saveKey?: string,
): Promise<unknown> {
    const form = new FormData();
    form.append('role', options.role);
    if (options.photo) form.append('photo', options.photo);
    if (options.sortOrder !== undefined) form.append('sort_order', String(options.sortOrder));
    const response = await erpFetch(`${PRODUCT_BASE}/masters/${masterId}/assets${productEditContext(procurementLineId)}`, {
        method: 'POST',
        headers: productSaveHeaders(saveKey),
        body: await directMediaForm(`${PRODUCT_BASE}/masters/${masterId}/assets${productEditContext(procurementLineId)}`,form),
    });
    if (!response.ok) throw await apiError(response);
    return response.json();
}

export async function replaceProductMasterImageAsset(
    masterId: number,
    assetId: number,
    photo: File,
    procurementLineId?: number,
    saveKey?: string,
): Promise<unknown> {
    const form = new FormData();
    form.append('photo', photo);
    const response = await erpFetch(`${PRODUCT_BASE}/masters/${masterId}/assets/${assetId}/replace${productEditContext(procurementLineId)}`, {
        method: 'POST',
        headers: productSaveHeaders(saveKey),
        body: await directMediaForm(`${PRODUCT_BASE}/masters/${masterId}/assets/${assetId}/replace${productEditContext(procurementLineId)}`,form),
    });
    if (!response.ok) throw await apiError(response);
    return response.json();
}

export const updateProcurementLine = (orderId: number, lineId: number, payload: {
    target_quantity?: number;
    notes?: string;
    supplier_id?: number | null;
    supplier_sku?: string | null;
    purchase_price?: number;
    purchase_currency?: string;
}) => request<ApiProcurementLineConfirmation>(`/procurements/${orderId}/lines/${lineId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const confirmLinePurchase = (orderId: number, lineId: number, payload: {
    purchased_quantity: number;
    purchase_price: number | null;
    purchase_currency?: string;
}) => request<ApiProcurementLineConfirmation>(`/procurements/${orderId}/lines/${lineId}/purchase`, { method: 'POST', body: JSON.stringify(payload) });

export type PurchaseAcceptanceEntry={line_id:number;purchased_quantity:number;purchase_price:number|null;purchase_currency?:string};
export const confirmLinesPurchase=(orderId:number,lines:PurchaseAcceptanceEntry[])=>
    request<PurchaseStatusConfirmation>(`/procurements/${orderId}/lines/purchase`,{method:'POST',body:JSON.stringify({lines})});

// One receipt carries its evidence, measurements and stable retry key.
export type ReceiptMeasurements = Record<'height'|'width'|'depth'|'internal_height'|'internal_width'|'internal_depth', number> & {unit:string};
/** Legacy cloud receipt jobs; new receipts use receipt_label_tasks in the shared queue. */
export interface ReceiptLabelData {sku:string;aisle:number;bin:number;qrcode:string}
/** Stable receipt obligations, persisted before automatic printing. */
export type ApiReceiptLabelTask={key:string;product_master_id:number;sku:string;quantity:number;source:import('../features/erp/labelTaskSource').LabelTaskSource};
export type ApiReceiptConfirmation = ApiProcurementLineConfirmation & {order_status:ProcurementStatus; receipt_label_tasks?:ApiReceiptLabelTask[]; receipt_print_jobs?:{status:string;job_ids:number[];label?:ReceiptLabelData}};
export const receiveLine = (orderId:number, lineId:number, payload:{receive_quantity:number;receipt_key:string;photo_id:number;measurements:ReceiptMeasurements;brand?:string;label_preview?:boolean}) =>
    request<ApiReceiptConfirmation>(`/procurements/${orderId}/lines/${lineId}/receive`, {method:'POST',body:JSON.stringify(payload)});
export const prepareReceiptPhoto = async (orderId:number, photo:File, identify:boolean) => {
    if(!identify)return (await import('../features/erp/productEntryOutbox')).reserveReceiptCapture(orderId,photo);
    return postProcurementPhoto<{photo_id:number;candidates:Array<ApiImageMatchCandidate & {line_id:number}>;error?:string}>(`/procurements/${orderId}/receipt-photo`,photo,{identify:String(identify)});
};
export const getReceiptLabelStatus = (orderId:number, ids:number[]) => request<{pending:number;failed:number}>(`/procurements/${orderId}/receipt-labels?ids=${ids.join(',')}`);
export const retryReceiptLabels = (orderId:number, ids:number[]) => request(`/procurements/${orderId}/receipt-labels/retry`,{method:'POST',body:JSON.stringify({ids})});
/** The receiver chose 发送到打印机 in the print preview: send the labels held for this receipt. */
export const releaseReceiptLabels = (orderId:number, ids:number[]) => request<{pending:number;failed:number}>(`/procurements/${orderId}/receipt-labels/release`,{method:'POST',body:JSON.stringify({ids})});

export const verifyReceiveLineImageMatch = (orderId: number, lineId: number, photo: File) =>
    postProcurementPhoto<ApiReceiveImageMatchResponse>(`/procurements/${orderId}/lines/${lineId}/receive-image-match`, photo);

type PurchaseStatusConfirmation = {
    lines: ApiProcurementLineConfirmation[];
    order_status: ApiProcurementOrder['status'];
};
const changePurchaseLineStatus = (orderId:number,lineIds:number[],action:string) =>
    request<PurchaseStatusConfirmation>(`/procurements/${orderId}/lines/${action}`,{method:'POST',body:JSON.stringify({line_ids:lineIds})});
export const markLinesException = (orderId:number,lineIds:number[]) => changePurchaseLineStatus(orderId,lineIds,'mark-exception');
export const closeLinesShort = (orderId:number,lineIds:number[]) => changePurchaseLineStatus(orderId,lineIds,'close-short');
export const markLinesOutOfStock = (orderId:number,lineIds:number[]) => changePurchaseLineStatus(orderId,lineIds,'out-of-stock');
/** 美国仓备货 待到仓 → 已完成: stock the Shanghai-received units in the US warehouse. */
export const arriveLines = (orderId:number,lineIds:number[]) => changePurchaseLineStatus(orderId,lineIds,'arrive');


export const getV04MerchandisingPreview = (projectId: number, projectionVersion?: string, visualVersion?: string, profileVersion?: string, selectionSeed?: string, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (projectionVersion) query.set('projection_version', projectionVersion);
    if (visualVersion) query.set('visual_version', visualVersion);
    if (profileVersion) query.set('profile_version', profileVersion);
    if (selectionSeed) query.set('selection_seed', selectionSeed);
    return request<ApiMerchandisingPreview>(`/ai-planning/projects/${projectId}/v0.4-merchandising-preview?${query}`, {signal});
};

export const generateLivePlanDraft = (projectId: number, payload: {
    planning_brief?: string;
    selection_mode?: 'free_text' | 'template';
    procurement_order_ids?: number[];
    sales_theme_enabled?: boolean;
    sales_theme_count?: number;
    profile_version?: string;
    projection_version?: string;
    visual_version?: string;
    selection_seed?: string;
    warehouse_id: number;
}) => request<ApiLivePlanDraft>(`/ai-planning/projects/${projectId}/live-plan-drafts`, {
    method: 'POST', body: JSON.stringify(payload),
});

export const applyLivestreamAiPlan = (liveId: number, payload: { application_token: string; mode: 'groups' | 'groups_and_skus' }) =>
    request<ApiLivestreamAiPlanApplyResult & { plan_id: string; idempotent: boolean }>(`/livestreams/${liveId}/ai-plan-applications`, {
        method: 'POST', body: JSON.stringify(payload),
    });

export interface ApiProcurementProductDetail {
    pricing?:ProductPricing;
    /** open = 接单前, accepted = 已接单（快照）, received = 已收货（库存资料）. */
    state: 'open' | 'accepted' | 'received';
    detail: ApiInventoryDetail;
    read_only: boolean;
    /** 与后端只读判定同源，不增加读取。旧后端未返回时按已有事实展示。 */
    read_only_reason?: '采购已完成' | '花盆已接单' | '由 WITHPOT 维护' | '由供应商维护' | null;
    snapshot_missing: boolean;
    /** 货盘资料：接单前为供应商当前货盘，已接单为接单时冻结的货盘快照；否则为空。 */
    pool_product: SupplierProduct | null;
    received_quantity: number;
    purchase: { quote_product_id?: number | null; supplier_id?: number | null; supplier_name?: string | null; supplier_sku?: string | null; accepted_at: string | null; purchased_quantity: number; purchase_price: number | null; purchase_currency: string | null };
}
export const getProcurementProductDetail = (orderId: number, lineId: number, signal?: AbortSignal) =>
    request<ApiProcurementProductDetail>(`/procurements/${orderId}/lines/${lineId}/product-detail`, { signal });
export interface TotalStockCorrection {
    warehouse_id:number;
    on_hand_quantity:number;
    expected_on_hand_quantity:number;
}

/** Visible livestream data is deliberately not an ApiLivestream workspace. */
export interface ApiLiveScreenItem extends ApiProductTitle, Partial<ApiProductPosterState>, Pick<ApiLivestreamItem,
    'selected_route' | 'supplier_purchase_price' | 'target_purchase_price_0_margin' |
    'target_purchase_price_profit' | 'target_profit_usd' | 'expected_profit' | 'expected_margin_rate'> {
    sequence_number?: number;
    giveaway_sessions?: Array<{id:number;scheduled_at:string|null}>;
    missing_purchase_fields?:string[];
    transfer_unaccepted?:boolean;
    can_rename?:boolean;
    selection_metadata?: ApiLivestreamSelectionMetadata;
    procurement_status?: LineStatus | null;
    product_name?: string | null;
    id: number; product_master_id: number; name: string; sku: string;
    group_id?: number | null; group_name: string | null; reserved_quantity: number; sold_quantity: number;
    final_listing_price: number | null; thumbnail_url: string | null; image_variants?:ImageVariants|null;
    system_standard_sale_price?: number | null; final_listing_price_currency?: string | null;
    internal_height?:number|null;internal_width?:number|null;internal_depth?:number|null;
    reserved_fulfilled_quantity?: number; height?: number | null; width?: number | null; depth?: number | null; unit?: string;
}
export interface ApiLiveScreen {
    product_index?: [number,number][];
    view_settings?: Record<string,string>;
    livestream: { has_reviewed_order?:boolean;picking_views?:Array<'selection'|'purchase'>;order_revision?:number;order_edit_revision?:number;order_group_by?:string;scheduled_at?:string|null;document_sessions?:Array<{id:number;is_returned?:boolean;has_reviewed_order?:boolean}>; supplier_collaboration?:SupplierCollaboration|null; picking_suppliers?:Array<{id:number;name:string}>; frozen_group_by?:'custom'|'none'|null; selection_sources?:Array<'inventory'|'pool'>; sellable_item_count?:number; sellable_quantity?:number; sold_item_count?:number; supplier_id?:number|null; brand_ids?:number[]; series?:LivestreamSeries|null; warehouse_id?:number|null; series_type?:'live'|'purge'; warehouse_name?:string|null; warehouse_code?:string|null; procurement_started?: boolean; sold_quantity?: number; can_start_labeling?: boolean; needs_procurement?: boolean; id: number; name: string; title?: string | null; status: ApiLivestream['status']; item_count?: number; reserved_quantity?: number; manual_order_initialized?: boolean };
    groups?: { id: number | null; name: string; description?: string | null; count: number; reserved_quantity?: number; positions:number[] }[];
    sections?: { key: string; title: string; description?: string | null; start: number; count: number; reserved_quantity?: number }[];
    items: ApiLiveScreenItem[]; page: number; per_page: number; total: number; pages: number; view: 'screen';
}
/** Initial live drawer projection, including its locally remembered sorting. */
export function liveScreenOptions(id:number,status:ApiLivestream['status']) {
    const saved=readListSettings(`live-view:${id}`);
    const values={sort:'added:desc',quantity:'',productStatus:'',groupId:'',viewMode:'grid',groupBy:'custom'};
    for(const key of Object.keys(values) as Array<keyof typeof values>)if(typeof saved[key]==='string')values[key]=saved[key] as string;
    return {sharedView:true,canonicalOrder:true,viewOptions:{...values,groupId:'',...liveProductFilters(values,status)}};
}
export function getLiveScreen(id: number, page: number, q: string, signal?: AbortSignal, options?: { sort?: string; quantity?: string; groupId?: string; canonicalOrder?:boolean; sharedView?: boolean; viewOptions?: Record<string,string> }) {
    const params = new URLSearchParams({ view: 'screen', page: String(page), per_page: '20' });
    Object.entries(productSortRates(options?.viewOptions?.sort??options?.sort)).forEach(([key,value])=>params.set(key,value));
    if(options?.canonicalOrder)params.set('canonical_order','1');
    if(options?.sharedView){params.set('shared_view','1');params.set('view_options',JSON.stringify(options.viewOptions ?? {}));}
    if (q.trim()) params.set('q', q.trim());
    if (options?.sort) params.set('sort', options.sort);
    if (options?.quantity) params.set('quantity', options.quantity);
    if (options?.groupId) params.set('group_id', options.groupId);
    return request<ApiLiveScreen>(`/livestreams/${id}?${params}`, { signal });
}

/** Browsing must never masquerade as a complete editable purchase order. */

export type LiveCreationDefaults = {warehouse_id?:number;series?:LivestreamSeries};
export const createLiveDraft = (scheduledAt: string | null = null, seriesType?: 'live' | 'purge', defaults?:LiveCreationDefaults) => request<ApiLivestreamEdit & {_creation_token?:string}>('/livestreams?view=fields', {
    method: 'POST', body: JSON.stringify({_transient: true, status: 'pending', ...defaults, ...(seriesType ? {series_type: seriesType} : {}), ...(seriesType === 'purge' ? {scheduled_at:scheduledAt} : {sessions:scheduledAt ? [{scheduled_at: scheduledAt}] : []})}),
});

export type LiveSalesMetrics = { revenue: number; cost: number | null; profit: number | null; margin: number | null; quantity: number; order_count: number };
export interface ApiLiveStatistics {
    summary: ApiLivestream;
    selection: import('../components/business/details/Quantity').ErpBucketSummaryItem[];
    sessions: (ApiLivestreamSession & {sales: LiveSalesMetrics | null})[];
}
export const getLiveStatistics = (id: number, signal?: AbortSignal) =>
    request<ApiLiveStatistics>(`/livestreams/${id}?view=statistics`, {signal});

/** An anchor move is atomic even when the client holds only one page. */
export const moveLivestreamItem = (liveId: number, baseSort: string, move: {item_id: number; target_id?: number; group_id: number | null; after: boolean; grouped?: boolean}, revision?:number) =>
    request<ApiLivestreamReorderResult & {restored: boolean;order_revision:number}>(`/livestreams/${liveId}/items/reorder`, {
        method: 'PATCH', body: JSON.stringify({base_sort: baseSort, move, revision}),
    });


export type LiveWorkSection = 'preparation' | 'performance' | 'review';
export type LiveWorkMedia = {design_target?:string;design_session_id?:number;design_job_id?:number;id?:string;poster_url?:string;download_url?:string;url: string; name: string; type: string; size: number};
export type LiveWorkValue = string | boolean | LiveWorkMedia | LiveWorkMedia[];
export type LiveWorkData = {session_id?:number|null;livestream_id: number; section: LiveWorkSection; values: Record<string, LiveWorkValue>};
export const getLiveWork = (id: number, section: LiveWorkSection, signal?: AbortSignal, sessionId?:number) =>
    request<LiveWorkData>(`/livestreams/${id}/work/${section}${sessionId ? `?session_id=${sessionId}` : ''}`, {signal});
export const saveLiveWork = (id: number, section: LiveWorkSection, key: string, value: LiveWorkValue, sessionId?:number) =>
    request<LiveWorkData>(`/livestreams/${id}/work/${section}/${key}?view=fields${sessionId ? `&session_id=${sessionId}` : ''}`, {method:'PUT', body:JSON.stringify({value})});
export async function uploadLiveWork(id:number,field:string,file:File,replaceId?:string,sessionId?:number):Promise<LiveWorkData> {
    return uploadLiveWorkFiles(id,field,[file],undefined,replaceId,sessionId);
}
export async function uploadLiveWorkFiles(id:number,field:string,files:File[],onSaved?:(data:LiveWorkData)=>void,replaceId?:string,sessionId?:number):Promise<LiveWorkData> {
    if(!files.length)throw new Error('请选择媒体文件');
    if(sessionId&&files.length!==1)throw new Error('每种素材只能上传一个文件');
    const endpoint=`${BASE}/livestreams/${id}/work-media/${field}?view=fields${sessionId ? `&session_id=${sessionId}` : ''}`;
    const owner=localStorage.getItem('supplier_portal_session');
    const check=()=>{if(owner!==localStorage.getItem('supplier_portal_session'))throw new DOMException('Session changed','AbortError');};
    const post=async(body:unknown)=>{
        check();const response=await erpFetch(endpoint,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
        if(!response.ok)throw new ApiRequestError(responseErrorMessage(response,await responseErrorBody(response)),response.status,null);
        const result=await response.json();check();return result;
    };
    let saved:LiveWorkData|undefined;
    // Prepare independent objects together; commit the collection in selection order.
    for(let offset=0;offset<files.length;offset+=MEDIA_UPLOAD_CONCURRENCY) {
        const batch:File[]=[];
        for(const file of files.slice(offset,offset+MEDIA_UPLOAD_CONCURRENCY))batch.push(await preparePrivateImage(file));
        check();
        const {tickets}=await post({uploads:batch.map(file=>({operation:crypto.randomUUID?.() ?? Array.from(crypto.getRandomValues(new Uint8Array(16)),byte=>byte.toString(16).padStart(2,'0')).join(''),name:file.name,size:file.size,type:file.type,replace_id:replaceId}))});
        if(!Array.isArray(tickets)||tickets.length!==batch.length)throw new Error('未收到上传凭证，请重试');
        const results=await Promise.allSettled(batch.map((file,index)=>uploadMedia(file,tickets[index],undefined,file.name)));
        const failed=results.find((result):result is PromiseRejectedResult=>result.status==='rejected');
        if(failed)throw failed.reason;
        for(const ticket of tickets){saved=await post({ticket:ticket.ticket});onSaved?.(saved!);}
    }
    return saved!;
}

export interface ReturnedOrder {
    review_state?:'pending'|'reviewed'|'cancelled';review_revision?:number;needs_organizing?:boolean;
    id: number; import_id: number; order_number: string; buyer: string | null;
    order_date: string | null; returned_at: string | null; quantity: number; fulfilled_quantity?:number; shortage_quantity?:number; amount: number|null;
    currency: string; line_count: number; livestream_id: number; batch: string | null;
    warehouse_name?: string | null;
    series_title: string | null; series_type: 'live' | 'purge'; series?: LivestreamSeries | null; session_id: number | null; session_time: string | null;
}
export interface ReturnedOrderLine extends ApiProductTitle, Partial<ApiProductPosterState> {livestream_item_id?:number|null;raw_item_title?:string|null;source_row_number?:number|null;supplier_reservations?:boolean;supplier_reserved_quantity?:number;
    internal_height?:number|null;internal_width?:number|null;internal_depth?:number|null;
    can_rename?:boolean;height?:number|null;width?:number|null;depth?:number|null;unit?:string|null;
    id:number;product_master_id:number;normalized_sku:string;product_name?:string | null;thumbnail_url?:string|null;image_variants?:ImageVariants|null;
    quantity:number;fulfilled_quantity:number | null;available_quantity?:number;temporarily_added?:boolean;missing_purchase_fields?:string[];purchase_ready?:boolean;
}

export interface ReturnedOrderFilter {livestream_id: number; name: string | null; title: string | null; series_type: 'live' | 'purge'; session_id: number | null; scheduled_at: string | null}
export const getReturnedOrders = (params: Record<string, string | number>, signal?: AbortSignal) =>
    request<{items: ReturnedOrder[]; total: number; pages: number;sections:{key:string;title:string;start:number;count:number}[]}>(`/returned-orders?${new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)]))}`, {signal});
export type ReturnedOrderSection = 'products' | 'customer' | 'other';
export interface PendingOrderProduct extends Partial<Omit<ReturnedOrderLine,'quantity'>> {sku:string;listing_row?:number;quantity:number|null;reason?:string;product_name?:string|null}
export interface ReturnedOrderProducts {id:number;listing_count?:number|null;listing_quantity?:number|null;revision:number;amount:number|null;currency:string;raw_item_title?:string|null;items:ReturnedOrderLine[];pending_products?:PendingOrderProduct[]}
export interface ReturnedOrderFields {id:number;[key:string]:string | number | null}
export const getReturnedOrder = (id: number, signal?: AbortSignal) => request<ReturnedOrderProducts>(`/returned-orders/${id}?section=products`, {signal});
export const getReturnedOrderFields = (id:number,section:'customer'|'other',signal?:AbortSignal) => request<ReturnedOrderFields>(`/returned-orders/${id}?section=${section}`,{signal});
export const saveReturnedOrderField = (id:number,key:string,value:string|number|null) => request<ReturnedOrderFields>(`/returned-orders/${id}/fields`,{method:'PATCH',body:JSON.stringify({[key]:value})});
export type ReturnedOrderProductsSaved = Omit<ReturnedOrderProducts, 'amount' | 'currency'> & Partial<Pick<OrderReview,'review_state'|'review_revision'|'needs_organizing'|'issues'>> & {order_number?:string;livestream_id?:number; temporary_labels?:ApiTemporaryLabel[]};
export const saveReturnedOrderProducts = (id:number,revision:number,items:{sku:string;quantity:number|null}[],requireStock=false,confirmedSku?:string,topUpSku?:string) => request<ReturnedOrderProductsSaved>(`/returned-orders/${id}/products`,{method:'PUT',body:JSON.stringify({revision,items:items.map(orderProductInput),...(requireStock?{require_stock:true}:{}),...(confirmedSku?{confirmed_sku:confirmedSku}:{}),...(topUpSku?{top_up_sku:topUpSku}:{})})});
export interface OrderReview {id:number;review_state:'pending'|'reviewed'|'cancelled';review_revision:number;needs_organizing:boolean;issues:{key:string;message:string;kind:'information'|'product'|'stock'}[];[key:string]:unknown}
export const getOrderReview=(id:number,signal?:AbortSignal)=>request<OrderReview>(`/returned-orders/${id}?section=review`,{signal});
export const getOrderReviewCount=(signal?:AbortSignal)=>request<{count:number;organizing:number;pending:number}>('/returned-orders/review-count',{signal});
export const resolveLabelTaskSources=(sources:{kind:string;id:number}[])=>request<{items:{kind:string;id:number;reference:string|null;display_name?:string|null}[]}>('/print-task-sources/batch',{method:'POST',body:JSON.stringify({sources})});
export type OrderLabelReceipt = {id:number;order_number?:string;revision:number;livestream_id:number;temporary_labels:ApiTemporaryLabel[]};
export type OrderTopUpResult = {items:(Pick<ReturnedOrder,'id'> & Partial<ReturnedOrder>)[];skipped:{id:number;reason:'cancelled'|'changed'|'complete';message:string}[];label_tasks:OrderLabelReceipt[]};
export const topUpOrders=(orders:{id:number;revision:number}[])=>request<OrderTopUpResult>('/returned-orders/top-up',{method:'POST',body:JSON.stringify({orders})});
export const reviewOrders=(orders:{id:number;revision:number}[],state:'pending'|'reviewed'|'cancelled')=>request<{items:ReturnedOrder[];skipped:{id:number;message:string}[]}>('/returned-orders/review',{method:'POST',body:JSON.stringify({orders,state})});
export const getOrderReturnTargets = (q:string,signal?:AbortSignal,sessionId?:number) => request<(ReturnedOrderFilter & {status:LivestreamStatus;warehouse_id:number;returned_at:string|null;returned_import_id:number|null;streamer_id:number|null;director_id:number|null})[]>(`/returned-orders/filters?${new URLSearchParams({scope:'return',q,...(sessionId?{session_id:String(sessionId)}:{})})}`,{signal});
export const getReturnedOrderFilters = (signal?: AbortSignal) => request<ReturnedOrderFilter[]>('/returned-orders/filters', {signal});

export const createEmptyPurchase = () => request<ApiProcurementDetail & {_creation_token:string}>('/procurements',{method:'POST',body:JSON.stringify({_transient:true})});
export const settleRecordCreation = (resource:import('../features/erp/creationRecords').CreationResource, id:number, token:string, retain=false) => {
    if(resource!=='live'&&resource!=='purchase'&&resource!=='inventory')return request<{discarded?:boolean}>(`/settings-records/${resource}/${id}/${retain?'retain-creation':'discard-empty'}`,{method:'POST',body:JSON.stringify({token})});
    const path = {live:'livestreams',purchase:'procurements',inventory:'inventory'}[resource];
    return request<{discarded?:boolean}>(`/${path}/${id}/${resource==='inventory'?(retain?'retain-creation':'discard-empty'):(retain?'retain-creation-draft':'discard-empty-draft')}`,{method:'POST',body:JSON.stringify({token})});
};


let productSaveSupport: Promise<void> | undefined;
export const requireProductSaveSupport = (): Promise<void> => productSaveSupport ??= request<{protocol:string}>('/product-save-support').then(result => {
    if (result.protocol !== 'product-save-receipt-v1') throw new Error('保存服务尚未就绪');
}).catch(error => { productSaveSupport = undefined; throw error; });

/** One transaction for the shared detail editor's database fields. */
export const saveProductDetail = (productId: number, data: {
    product: Parameters<typeof updateProductMaster>[1];
    procurement?: {order_id: number; line_id: number; changes: Parameters<typeof updateProcurementLine>[2]};
    warehouses: TotalStockCorrection[];
}, saveKey: string) => request<{detail: Partial<Omit<ApiInventoryDetail, "product_master">> & {product_master?: Partial<ApiProductSummary>}; purchase: Partial<ApiProcurementProductDetail["purchase"]> | null}>(
    `/inventory/${productId}/save-detail`, {method: 'PATCH', headers: productSaveHeaders(saveKey), body: JSON.stringify(data)});

export type ApiLivestreamChoice = Pick<ApiLivestream, 'id'|'name'|'status'|'location_id'|'streamer_name'>;
export const getLivestreamChoices = (page: number, locationIds: number[], signal?: AbortSignal) =>
    request<{items: ApiLivestreamChoice[]; total: number; pages: number}>(`/livestreams?view=choices&status=pending&page=${page}&per_page=20&location_ids=${locationIds.join(',')}`, {signal});


export interface PurchaseTransferTarget {eligible_ids?:number[];id:number;name:string;kind:'purchase'|'live'}
export const getPurchaseTransferTargets = (id:number,q='',signal?:AbortSignal,selection?:{line_ids:string;mode:'copy'|'move'}) => request<{items:PurchaseTransferTarget[]}>(`/procurements/${id}/transfer-targets?${new URLSearchParams({q,...selection})}`,{signal});
export const transferPurchaseLines = (id:number,payload:{line_ids:number[];mode:'copy'|'move';target_kind:'purchase'|'live';target_id:number},receipt:string) => request<{id:number;transferred_count:number;removed_line_ids:number[];retained_line_ids:number[]}>(`/procurements/${id}/transfer`,{method:'POST',headers:receipt?{'X-Product-Save-Key':receipt}:undefined,body:JSON.stringify(payload)});
export const movePurchaseLine = (id:number,payload:{line_id:number;target_id?:number;after:boolean;grouped:boolean;revision?:number}) => request<{id:number;line_ids:number[];order_revision:number;view_settings:Record<string,string>}>(`/procurements/${id}/lines/reorder`,{method:'PATCH',body:JSON.stringify(payload)});

export const deletePurchaseLines = (id:number,lineIds:number[]) => request<{id:number;line_ids:number[]}>(`/procurements/${id}/lines/delete`,{method:'POST',body:JSON.stringify({line_ids:lineIds})});

export interface ApiReceiptRecord {id:number;quantity:number;created_at:string;photo_url?:string;measurements?:Record<string,number|string>;print_job_ids:number[]}

export interface ApiAiImageQueueItem extends Pick<ApiCoverPosterJob, 'id'|'status'|'headline_text'|'white_bg_url'|'template_url'|'final_poster_url'|'prompt_version'|'error_msg'|'created_at'|'updated_at'> {product_master_id:number|null;original_url?:string|null;}
export interface ApiAiImageQueue {items:ApiAiImageQueueItem[];total:number;page:number;pages:number;per_page:number;summary:{total:number;active:number;queued:number;failed:number};}
export interface ApiAiImageConfiguration {templates:Array<{key:string;name:string;prompt:string}>;preferences:Array<{key:string;kind:string;label:string;prompt:string}>;background_prompt:string;}
export const getAiImageQueue=(page=1,signal?:AbortSignal,query:{search?:string;status?:string;sort?:string}={})=>{
    const params=new URLSearchParams({page:String(page),per_page:'20'});
    Object.entries(query).forEach(([key,value])=>{if(value)params.set(key,value);});
    return request<ApiAiImageQueue>(`/image-tools/product-jobs?${params}`,{signal});
};
export const getAiImageConfiguration=(signal?:AbortSignal)=>request<ApiAiImageConfiguration>('/image-tools/product-configuration',{signal});
export const updateAiImagePrompt=(key:string,value:string)=>request<{key:string;value:string}>('/image-tools/product-configuration',{method:'PATCH',body:JSON.stringify({key,value})});

export const getPackagingScheme=(signal?:AbortSignal,fresh=false)=>request<import('../lab/packaging/contract').PackagingScheme>('/packaging/scheme',{signal},fresh);
export const matchPackaging=(dimensions:import('../lab/packaging/contract').PackagingInput,signal?:AbortSignal)=>request<import('../lab/packaging/contract').PackagingMatch>('/packaging/match',{method:'POST',body:JSON.stringify(dimensions),signal});


export interface ApiProductPriceHistory {
    pricing_rule?: {config:ApiPricingRuleConfig;version:string};
    current: {price:number|null;currency:string}|null;
    cost: {price:number|null;currency:string}|null;
    items: Array<{id:number;price:number|null;currency:string;changed_at:string;reason:string;reference_code:string|null;reference_label?:string|null}>;
    total: number;
    next_cursor: number|null;
}
export const getProductPriceHistory = (identity:{product_master_id?:number;supplier_product_id?:number}, kind:'cost'|'sale', before?:number, signal?:AbortSignal) => {
    const params=new URLSearchParams({kind});
    Object.entries(identity).forEach(([key,value])=>{if(value!=null)params.set(key,String(value));});
    if(before!=null)params.set('before',String(before));
    return request<ApiProductPriceHistory>(`/product-prices?${params}`,{signal});
};
export const updateProductSalePrice = (id:number, price:number|null, currency:string, saveKey:string, reason:'inventory'|'calculation'='inventory') =>
    request<{price:number|null;currency:string}>(`/inventory/${id}/sale-price`,{
        method:'PATCH',headers:{'Content-Type':'application/json','X-Product-Save-Key':saveKey},body:JSON.stringify({price,currency,reason})});

export const updatePoolSalePrice = (id:number,price:number|null,currency:string,saveKey:string) =>
    request<{price:number|null;currency:string}>(`/supplier-products/${id}/sale-price`,{method:'PATCH',headers:productSaveHeaders(saveKey),body:JSON.stringify({price,currency})});

export const updatePackagingScheme=(changes:import('../lab/packaging/contract').PackagingPatch)=>request<import('../lab/packaging/contract').PackagingScheme>('/packaging/scheme',{method:'PATCH',body:JSON.stringify(changes)});


export const saveProductFields=(id:number,product:Parameters<typeof updateProductMaster>[1])=>request<Awaited<ReturnType<typeof saveProductDetail>>>(`/inventory/${id}/fields`,{method:'PATCH',body:JSON.stringify(product)});
export const updateProductCost=(id:number,price:number|null,currency:string,key:string)=>request<{price:number|null;currency:string}>(`/inventory/${id}/cost`,{method:'PATCH',headers:productSaveHeaders(key),body:JSON.stringify({price,currency})});


export type ApiQuickProductResult={receipt_label_tasks?:ApiReceiptLabelTask[];livestream_id?:number;temporary_labels?:ApiTemporaryLabel[];order_products?:ReturnedOrderProductsSaved;order_draft?:ApiLivestreamOrderImportPreview;line?:ApiProcurementLine;item?:ApiLivestreamItemConfirmation;product_master_id?:number;sku?:string};
export type ApiProductEntryResult=ApiQuickProductResult & {photo_id?:number;pool_id?:number;supplier_id?:number;client_id?:string|null;created_at?:string|null};
export const createProductEntry=async(form:FormData,key:string):Promise<ApiProductEntryResult>=>{
    const res=await erpFetch(`${BASE}/product-entries`,{method:'POST',headers:productSaveHeaders(key),body:form});
    if(res.ok)return res.json();throw await apiError(res);
};


export interface ProductCandidate extends ApiProductTitle, ApiProductSale, Partial<ApiProductPosterState> {
    internal_height?:number|null;internal_width?:number|null;internal_depth?:number|null;
    height?:number|null;width?:number|null;depth?:number|null;unit?:string;
    id:string;source:'inventory'|'pool';source_id:number;product_master_id:number|null;
    name:string|null;sku:string;supplier_id?:number;supplier_name?:string|null;maintenance?:'supplier'|'withpot';
    price:number|null;currency:string;available_quantity:number;current_quantity:number;already_selected?:boolean;
    image_url?:string|null;image_variants?:ImageVariants|null;purchase_url?:string|null;purchase_ready:boolean;missing_purchase_fields:string[];
}
export interface ProductCandidatePage {warehouse_code?:string|null;sku_exists?:boolean|null;suppliers?:Array<{id:number;name:string}>;warehouse_id?:number;sources?:('inventory'|'pool')[];items:ProductCandidate[];total:number;pages:number;editable:boolean;blocked_reason?:string|null}
export const getProductCandidates=(kind:'live'|'purchase',id:number,params:Record<string,string>,signal?:AbortSignal)=>
    request<ProductCandidatePage>(`/${kind==='live'?'livestreams':'procurements'}/${id}/selectable-products?${new URLSearchParams(params)}`,{signal});

export interface ProductTransferItem {source:'inventory'|'pool';id:number;quantity:number;warehouse_id?:number}
export interface ProductTransferTarget {eligible_ids?:number[];kind:'live'|'purchase'|'supplier';id:number;name:string}
export interface ProductPlanStock {id:number;available:number;selected?:boolean}
export interface ProductPlanTarget {kind:'live'|'purchase';id:number;name:string|null;number:string|null;warehouse:string|null;count:number;quantity:number;status:string;all_selected?:boolean;warehouse_id?:number;available_quantity?:number|null;stock?:ProductPlanStock[]}
// Large selections belong in the request body, never a proxy-limited URL.
function readProductTargets<T>(params:Record<string,string|number>,signal?:AbortSignal):Promise<T> {
    const body=JSON.stringify(params);
    return readQuery(`raw|${BASE}/product-selection/targets|${body}`,
        readSignal=>request<T>('/product-selection/targets/search',{method:'POST',body,signal:readSignal}),
        {signal,staleTime:3_000});
}
export const getProductPlanTargets=(params:Record<string,string|number>,signal?:AbortSignal)=>
    readProductTargets<{items:ProductPlanTarget[];total:number;pages:number;warehouses:{id:number;name:string}[]}>({view:'plans',...params},signal);
export const getProductTransferTargets=(signal?:AbortSignal,query?:{scope?:'supplier';q:string;pool_ids?:string;mode?:'copy'|'move'})=>
    readProductTargets<{items:ProductTransferTarget[]}>(query??{},signal);
export const transferProductSelection=(items:ProductTransferItem[],target:string,receipt:string,mode:'copy'|'move'='copy',onDuplicate:'skip'|'increment'='skip')=>{
    const [kind,id]=target.split(':');
    return request<{product_master_id?:number;created_count?:number;updated_count?:number;skipped_count?:number;target_count?:number;target_quantity?:number;warehouse_id?:number;available_quantity?:number;stock?:ProductPlanStock[]}>('/product-selection/transfer',{
        method:'POST',headers:{'X-Product-Save-Key':receipt},body:JSON.stringify({items,target_kind:kind,target_id:Number(id)||undefined,mode,...(onDuplicate==='increment'?{on_duplicate:onDuplicate}:{})}),
    });
};

export interface ApiLivestreamPlanImportRow {
    row_number: number;
    sku: string;
    normalized_sku: string;
    run_order: number | null;
    quantity: number;
    cover_candidate: boolean;
    item_role: string;
    topic_segment: string;
    group_name: string;
    short_reason: string;
    comment: string;
    product_master_id: number | null;
    product: ApiSelectableItem | null;
    errors: string[];
    warnings: string[];
}

export interface ApiLivestreamPlanImportPreview {
    valid: boolean;
    livestream_id: number;
    rows: ApiLivestreamPlanImportRow[];
    valid_rows: ApiLivestreamPlanImportRow[];
    invalid_rows: ApiLivestreamPlanImportRow[];
    totals: {
        row_count: number;
        valid_count: number;
        invalid_count: number;
        quantity_total: number;
        cover_candidate_count: number;
    };
    groups: Array<{ name: string; item_count: number; exists: boolean }>;
}

export interface ApiLivestreamPlanImportCommitResult {
    livestream: ApiLivestreamConfirmation;
    added_count: number;
    updated_count: number;
    metadata_count: number;
    reordered_count: number;
    cover_item_id: number | null;
}

export async function previewLivestreamPlanImport(
    liveId: number,
    file: File,signal?:AbortSignal
): Promise<ApiLivestreamPlanImportPreview> {
    const form = new FormData();
    form.append('file', file);
    return erpFormRequest<ApiLivestreamPlanImportPreview>(`/livestreams/${liveId}/plan-imports/preview`, form,undefined,signal);
}

export async function commitLivestreamPlanImport(
    liveId: number,
    file: File,requestKey?:string,signal?:AbortSignal
): Promise<ApiLivestreamPlanImportCommitResult> {
    const form = new FormData();
    form.append('file', file);
    return erpFormRequest<ApiLivestreamPlanImportCommitResult>(`/livestreams/${liveId}/plan-imports/commit`, form,requestKey?{'X-Product-Save-Key':requestKey}:undefined,signal);
}

export const getLiveSupplierOptions = (q:string,signal?:AbortSignal,storeLive=false) => request<{id:number;name:string}[]>(`/livestream-supplier-options?q=${encodeURIComponent(q)}${storeLive?'&store_live=1':''}`,{signal});

/** Global visual lookup: no selection destination, warehouse or stock eligibility. */
export interface SimilarProductReference {productId?:number|null;poolId?:number|null;sku?:string|null;imageUrl?:string|null}
export interface SimilarProductResult extends ApiProductTitle {product_master_id:number;sku:string;display_name:string;image_url:string;internal_height?:number|null;internal_width?:number|null;internal_depth?:number|null;unit?:string|null}
export const findSimilarProducts=(reference:SimilarProductReference,signal?:AbortSignal)=>{
    const query=new URLSearchParams();
    if(reference.poolId)query.set('pool_id',String(reference.poolId));
    else if(reference.productId)query.set('product_id',String(reference.productId));
    else if(reference.sku)query.set('sku',reference.sku);
    return request<{items:SimilarProductResult[]}>(`/products/similar?${query}`,homeReadOptions(signal));
};

// Customer service remains internal; no public token or portal endpoint is exposed.
export interface CustomerSummary {id:number;username:string;order_count:number;product_count:number;quantity:number;unfinished_count:number;complaint_status:ComplaintStatus|null;first_order_at:string|null;last_order_at:string|null}
export interface CustomerAccount {id:number;platform:string;site:string;username:string|null;email:string|null;revision:number;readonly:boolean;source_order_id:number|null}
export interface CustomerInformation {id:number;information:ReturnedOrderFields|null;addresses:ReturnedOrderFields[];purchase_totals:{currency:string;amount:number}[];accounts:CustomerAccount[];first_order_at:string|null;last_order_at:string|null}
export interface ComplaintType {id:number;name:string;remedy:string;enabled:boolean;revision:number;usage_count:number}
export type ComplaintStatus='normal'|'urgent'|'overdue'|'completed';
export interface CustomerComplaint {status:ComplaintStatus;can_set_normal:boolean;id:number;customer_id:number;type_id:number|null;type_name:string|null;occurred_at:string|null;description:string;handling?:string;resolved:boolean;resolved_at:string|null;order_id:number|null;order_number?:string|null;revision:number;remedy?:string|null;type_enabled?:boolean|null}
export interface CustomerProduct extends ApiInventoryItem {purchased_quantity:number}
export interface CustomerAmountEvent {id:number;order_id:number;order_number:string;kind:string;amount_before:number|null;amount_after:number|null;currency_before:string;currency_after:string;amount_delta:number;actor:string;created_at:string}
type CustomerPage<T>={items:T[];total:number;pages:number;sections?:{key:string;title:string;start:number;count:number}[]};
const customerQuery=(params:Record<string,string|number>)=>new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)])).toString();
export const getCustomers=(params:Record<string,string|number>={},signal?:AbortSignal)=>request<CustomerPage<CustomerSummary>>(`/customers?${customerQuery(params)}`,{signal});
export const getCustomer=(id:number,signal?:AbortSignal)=>request<CustomerInformation>(`/customers/${id}`,{signal});
export const getOrderAmountHistory=(id:number,params:Record<string,string|number>,signal?:AbortSignal)=>request<CustomerPage<CustomerAmountEvent>>(`/returned-orders/${id}/amount-history?${customerQuery(params)}`,{signal});
export const getCustomerProducts=(id:number,params:Record<string,string|number>,signal?:AbortSignal)=>request<ApiInventoryList>(`/customers/${id}/products?${customerQuery(params)}`,{signal});
export const getComplaintTypes=(params:Record<string,string|number>={},signal?:AbortSignal)=>request<CustomerPage<ComplaintType>>(`/complaint-types?${customerQuery(params)}`,{signal});
export const saveComplaintType=(values:Record<string,unknown>,id?:number)=>request<ComplaintType>(`/complaint-types${id?`/${id}`:''}`,{method:id?'PATCH':'POST',body:JSON.stringify(values)});
export const getCustomerComplaints=(id:number,params:Record<string,string|number>,signal?:AbortSignal)=>request<CustomerPage<CustomerComplaint>>(`/customers/${id}/complaints?${customerQuery(params)}`,{signal});
export const createCustomerComplaint=(id:number)=>request<CustomerComplaint>(`/customers/${id}/complaints`,{method:'POST',body:JSON.stringify({})});
export const saveCustomerComplaint=(id:number,values:Record<string,unknown>,qid?:number)=>request<Partial<CustomerComplaint>>(`/customers/${id}/complaints${qid?`/${qid}`:''}`,{method:qid?'PATCH':'POST',body:JSON.stringify(values)});
export const customerServiceAction=(kind:'types'|'complaints',id:number,records:{id:number;revision:number}[],action:string)=>request<{ids:number[];action:string}>(kind==='types'?'/complaint-types/actions':`/customers/${id}/complaints/actions`,{method:'POST',body:JSON.stringify({records:records.map(({id,revision})=>({id,revision})),action})});

export const createSettingsRecord = <T extends {id:number;_creation_token?:string}>(resource:'members'|'warehouses'|'complaintTypes'|'attributes'|'attributeOptions'|'categories',defaults:Record<string,unknown>={})=>request<T & {_creation_token:string}>(`/settings-records/${resource}`,{method:'POST',body:JSON.stringify(defaults)});

export type ProductBulkTarget={source:'pool'|'inventory';id:number};
export type ProductBulkContext={count:number;supplier_id:number|null;editable:boolean;purchase_count:number};
export type ProductBulkChanges={brand?:string;series?:string;sale_price?:number;sale_currency?:string;purchase_price?:number;height?:number;width?:number;depth?:number;internal_height?:number;internal_width?:number;internal_depth?:number;weight?:number;unit?:string;attribute_changes?:Record<string,string|string[]>};
export const prepareProductBulkEdit=(targets:ProductBulkTarget[],signal?:AbortSignal)=>request<ProductBulkContext>('/products/bulk-edit/prepare',{method:'POST',body:JSON.stringify({targets}),signal});
export const saveProductBulkEdit=(targets:ProductBulkTarget[],changes:ProductBulkChanges,key:string)=>request<{updated:number;purchase_updated:number;purchase_skipped:number}>('/products/bulk-edit',{method:'PATCH',headers:productSaveHeaders(key),body:JSON.stringify({targets,changes})});

export const getUnavailableBusinessDates = (kind:'live'|'purge'|'purchase', excludeId?:number) =>
    request<string[]>(`/business-numbers/unavailable-dates?${new URLSearchParams({kind,...(excludeId ? {exclude_id:String(excludeId)} : {})})}`, undefined, true);

// Supplier-series discovery persists Lab artifacts independently of catalog fields.
export interface ApiSeriesSource {
    id:number; name:string; product_count:number; ready_embeddings:number; evidence_products:number;
}
export interface ApiSeriesSnapshot {
    id:string;status:'queued'|'processing'|'ready'|'failed';error_message:string|null;captured_at?:string|null;
    product_count:number; content_hash:string; created_at?:string;
    scope:{supplier_ids:number[];suppliers:Array<{id:number;name:string}>;partition:'per_supplier'};
    manifest:{schema_version:string;model:{name:string;version:string;dimensions:number;role:string};
        states:Record<string,number>;evidence_products:number;label_products:number;unique_vectors:number};
}
export interface ApiSeriesRun {
    id:string;snapshot_id:string;status:'queued'|'processing'|'completed'|'failed';created_at:string;
    error_message:string|null;progress:Record<string,number>;diagnostics?:Record<string,unknown>;
    result_hash?:string|null;
}
export interface ApiSeriesGroup {
    id:string;supplier_id:number;name:string;product_count:number;
    representatives:Array<{id:string;supplier_product_id:number;item_code:string;image_url:string|null}>;
    evidence:{name_support:Record<string,unknown>;common_tokens:Record<string,string[]>;provisional:boolean};
}
export interface ApiSeriesProduct {
    id:string;product_id?:string;supplier_product_id:number;supplier_id:number;group_id?:string|null;
    status?:string;embedding_state?:string;image_url?:string|null;effective_evidence_id?:number|null;
    catalog:Record<string,unknown> & {item_code:string;name:string|null;front_url:string|null;detail_url:string|null;bottom_url:string|null};
    refinement:{tokens:Record<string,string[]>};
    diagnostics?:{reasons:string[];alternatives?:Array<{group_id:string;name:string;mean_distance:number}>;image_margin?:number|null};
}
const SERIES_PATH='/ai-planning/series-discovery';
export const getSeriesSources=(page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesSource>>(`${SERIES_PATH}/sources?page=${page}&per_page=30`,{signal});
export const getSeriesSnapshots=(supplierIds:number[],page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesSnapshot>>(`${SERIES_PATH}/snapshots?supplier_ids=${supplierIds.join(',')}&page=${page}`,{signal},true);
export const captureSeriesSnapshot=(supplierIds:number[],requestKey:string,signal?:AbortSignal)=>
    request<ApiSeriesSnapshot>(`${SERIES_PATH}/snapshots`,{method:'POST',body:JSON.stringify({supplier_ids:supplierIds,request_key:requestKey}),signal});
export const getSeriesSnapshotProducts=(id:string,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesProduct>>(`${SERIES_PATH}/snapshots/${id}/products?page=${page}`,{signal});
export const getSeriesRuns=(snapshotId:string,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesRun>>(`${SERIES_PATH}/snapshots/${snapshotId}/runs?page=${page}`,{signal},true);
export const queueSeriesRun=(snapshotId:string,requestKey:string,signal?:AbortSignal)=>
    request<ApiSeriesRun>(`${SERIES_PATH}/snapshots/${snapshotId}/runs`,{method:'POST',body:JSON.stringify({request_key:requestKey}),signal});
export const getSeriesRun=(id:string,signal?:AbortSignal)=>
    request<ApiSeriesRun>(`${SERIES_PATH}/runs/${id}`,{signal},true);
export const getSeriesGroups=(runId:string,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesGroup>>(`${SERIES_PATH}/runs/${runId}/groups?page=${page}`,{signal});
export const getSeriesMembers=(runId:string,groupId:string|null,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiSeriesProduct>>(`${SERIES_PATH}/runs/${runId}/members?${groupId?`group_id=${groupId}`:'unassigned=1'}&page=${page}`,{signal});
export const getSeriesProductEvidence=(snapshotId:string,productId:string,signal?:AbortSignal)=>
    request<{id:string;embedding_state:string;payload:{effective_evidence_id:number|null;effective_facts:Record<string,ApiPotFact>;source_memberships:Array<Record<string,unknown>>;
        evidence:Array<{id:number;job_id:number;prompt_version:string;model:string;status:string;generated_attributes:{facts?:Record<string,ApiPotFact>};attribute_suggestions:{revision?:number;effective_facts?:Record<string,ApiPotFact>}|null}>}}>(
            `${SERIES_PATH}/snapshots/${snapshotId}/products/${productId}`,{signal});

export interface ApiSeriesPage<T> {items:T[];total:number;pages:number;pagination:{page:number;per_page:number;total:number;pages:number};}

export const getSeriesSnapshot=(id:string,signal?:AbortSignal)=>request<ApiSeriesSnapshot>(`${SERIES_PATH}/snapshots/${id}`,{signal},true);

// Native Lab grouping: immutable inputs/results, explicit background commands.
export interface ApiGroupingExperiment {
    id:string;name:string;created_at:string;supplier:{id:number;name:string};product_count:number;
    split_counts:Record<string,number>;snapshot_id?:string|null;split_strategy?:string|null;dataset_hash:string;status:GroupingStatus;error_message?:string|null;
}
export interface ApiGroupingDetail extends ApiGroupingExperiment {
    goal:string;default_run_goal:string;source_kind:string;collage_count:number;
    input_accounting?:Record<string,{products:number;missing_image:number;missing_vector:number;missing_both:number}>|null;
}
export interface ApiGroupingProduct {
    product_id:number;item_code:string;name:string;split:string;image_state:string;has_vector:boolean;
    thumbnail_src:string|null;image_asset:string|null;group_id?:string|null;uncertain?:boolean;reason?:string;
}
export interface ApiGroupingCollage {
    id:string;tile_size:number;batch:number;width:number;height:number;request_hash:string;
    asset:string;product_count:number;thumbnail_src:string|null;tiles:{tile_id:string;product_id:number}[];
}
export interface ApiGroupingRun {
    id:string;method:'embedding_saved_facts'|'ai_saved_response';created_at:string;product_count:number;
    assigned:number;unassigned:number;cost_usd:number|null;
    model:string|null;prompt_version:string|null;tile_size:number|null;
    status:GroupingStatus;scope:string;error_message?:string|null;usage?:GroupingUsage;
    goal?:string|null;goal_hash?:string|null;merge_only_calibration?:boolean;
}
export interface ApiGroupingGroup {
    id:string;name:string;definition:string;include:string[];exclude:string[];product_count:number;
    representatives:ApiGroupingProduct[];
}
const GROUPING_PATH='/ai-planning/grouping-experiments';
export const getGroupingExperiments=(page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiGroupingExperiment>&{configured:boolean}>(`${GROUPING_PATH}?page=${page}`,{signal});
export const getGroupingExperiment=(id:string,signal?:AbortSignal)=>request<ApiGroupingDetail>(`${GROUPING_PATH}/${id}`,{signal});
export const getGroupingProducts=(id:string,page:number,signal?:AbortSignal)=>request<ApiSeriesPage<ApiGroupingProduct>>(`${GROUPING_PATH}/${id}/products?page=${page}`,{signal});
export const getGroupingCollages=(id:string,page:number,signal?:AbortSignal)=>request<ApiSeriesPage<ApiGroupingCollage>>(`${GROUPING_PATH}/${id}/collages?page=${page}`,{signal});
export const getGroupingRuns=(id:string,page:number,signal?:AbortSignal)=>request<ApiSeriesPage<ApiGroupingRun>>(`${GROUPING_PATH}/${id}/runs?page=${page}`,{signal});
export const getGroupingGroups=(id:string,run:string,page:number,signal?:AbortSignal)=>request<ApiSeriesPage<ApiGroupingGroup>>(`${GROUPING_PATH}/${id}/runs/${run}/groups?page=${page}`,{signal});
export const getGroupingMembers=(id:string,run:string,group:string|null,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<ApiGroupingProduct>>(`${GROUPING_PATH}/${id}/runs/${run}/members?${new URLSearchParams({page:String(page),...(group?{group_id:group}:{})})}`,{signal});
export const getGroupingMedia=(id:string,asset:string,signal?:AbortSignal)=>request<{src:string}>(`${GROUPING_PATH}/${id}/media/${encodeURIComponent(asset)}`,{signal});

export type GroupingStatus='queued'|'processing'|'completed'|'failed';
export interface GroupingUsage {cost_usd:number|null;known_cost_usd:number;unknown_calls:number;calls:number}
export interface GroupingJob {
    id:string;status:GroupingStatus;method:string;experiment_id:string;error_message:string|null;
    can_resume?:boolean;progress:{stage?:string;call_status?:string;completed?:number;total?:number};usage:GroupingUsage;
    calls:{call_id?:string;phase:string;status:string;response_id?:string|null;response_status?:string|null;incomplete_reason?:string|null;max_output_tokens?:number|null;
        usage:{raw?:{input_tokens?:number;output_tokens?:number;output_tokens_details?:{reasoning_tokens?:number}};
            cost_usd:number|null;unknown_reason?:string};http_status?:number}[];
}
export interface GroupingQuote {
    quote:{model:string;prompt_version:string;schema_version:string;max_output_tokens_per_call:number;goal:string;goal_hash:string;
        discovery_max_output_tokens?:number;reconciliation_max_output_tokens?:number;
        scope:'comparison'|'strong';tile_size:number;estimated_cost_usd:number;
        discovery_calls:number;reconciliation_calls:number;detail_calls:number;estimate_kind:string;
        pricing:{input:number;cached_input:number;cache_write:number;output:number;version:string;source:string}};
    quote_token:string;credential_configured:boolean;
}
export const prepareGrouping=(snapshot_id:string,request_key:string,signal?:AbortSignal)=>
    request<{id:string;status:GroupingStatus}>(GROUPING_PATH,{method:'POST',body:JSON.stringify({snapshot_id,request_key}),signal});
export const getGroupingJob=(id:string,signal?:AbortSignal)=>request<GroupingJob>(`${GROUPING_PATH}/jobs/${id}`,{signal},true);
export const resumeGroupingJob=(id:string,request_key:string,signal?:AbortSignal)=>
    request<{id:string;status:GroupingStatus}>(`${GROUPING_PATH}/jobs/${id}/resume`,{method:'POST',body:JSON.stringify({request_key,confirm_paid:true}),signal});
export const getGroupingQuote=(id:string,scope:'comparison'|'strong',tile_size:number,goal:string,signal?:AbortSignal)=>
    request<GroupingQuote>(`${GROUPING_PATH}/${id}/quote`,{method:'POST',body:JSON.stringify({scope,tile_size,goal}),signal},true);
export const queueGroupingRun=(id:string,request_key:string,method:'baseline'|'comparison'|'strong',tile_size:number,quote?:GroupingQuote,signal?:AbortSignal)=>
    request<{id:string;status:GroupingStatus}>(`${GROUPING_PATH}/${id}/runs`,{method:'POST',body:JSON.stringify({request_key,method,tile_size,
        ...(quote?{confirm_paid:true,goal:quote.quote.goal,quote:quote.quote,quote_token:quote.quote_token}:{})}),signal});
export type GroupingPlacementMethod='placement_candidates'|'placement_review'|'placement_confirmation';
export interface GroupingPlacementHistory {
    id:string;status:GroupingStatus;error_message:string|null;created_at:string;method:GroupingPlacementMethod;source_id:string;
    summary:{product_count?:number;usage?:GroupingUsage};
}
export interface GroupingPlacementProduct extends ApiGroupingProduct {
    id:number;reason?:string;
    candidates:{group_id:string;cosine_distance:number;centroid_distance:number|null;nearest_member_distance:number;shared_saved_facts:string[]}[];
    review?:{product_id:number;group_id:string|null;disposition:'match'|'no_match'|'needs_review';reason:string};
    call?:{status:string;cost_usd:number|null;unknown_reason?:string|null};
}
export interface GroupingPlacementPage extends ApiSeriesPage<GroupingPlacementProduct> {
    method:GroupingPlacementMethod;content_hash:string;initial_unassigned_excluded:number;
    requires_regeneration:boolean;
    binding:{snapshot_id:string;snapshot_hash:string;target_run_id:string;target_run_hash:string;group_version:string;version:string};
    group_set:{job_id:string;hash:string};
}
export interface GroupingPlacementGroups {hash:string;items:Omit<ApiGroupingGroup,'product_count'>[]}
export interface GroupingPlacementQuote {
    quote:{model:string;prompt_version:string;pricing:GroupingQuote['quote']['pricing'];source_id:string;source_hash:string;
        selection_hash:string;product_count:number;calls:number;image_count:number;candidate_groups_per_product:number;
        representatives_per_group:number;estimated_cost_usd:number;estimate_kind:string};
    quote_token:string;credential_configured:boolean;
}
const placementPath=(id:string,run:string)=>`${GROUPING_PATH}/${id}/runs/${run}/placements`;
export const getGroupingPlacementScope=(id:string,run:string,signal?:AbortSignal)=>
    request<{product_count:number;snapshot_id:string}>(`${placementPath(id,run)}/scope`,{signal});
export const getGroupingPlacements=(id:string,run:string,page:number,signal?:AbortSignal)=>
    request<ApiSeriesPage<GroupingPlacementHistory>>(`${placementPath(id,run)}?page=${page}`,{signal});
export const getGroupingPlacement=async(id:string,run:string,placement:string,page:number,signal?:AbortSignal)=>{
    const result=await request<GroupingPlacementPage>(`${placementPath(id,run)}/${placement}?page=${page}&view_version=3`,{signal});
    return {...result,items:result.items.map(row=>({...row,id:row.product_id}))};
};
export const getGroupingPlacementGroups=(id:string,run:string,placement:string,signal?:AbortSignal)=>
    request<GroupingPlacementGroups>(`${placementPath(id,run)}/${placement}/groups`,{signal});
export const getGroupingPlacementSelection=(id:string,run:string,placement:string,page:number,signal?:AbortSignal)=>
    request<{items:{id:number;product_id:number}[];total:number;pages:number}>(`${placementPath(id,run)}/${placement}/selection?page=${page}`,{signal});
export const getGroupingPlacementMedia=(id:string,run:string,placement:string,product:number,signal?:AbortSignal)=>
    request<{src:string}>(`${placementPath(id,run)}/${placement}/media/${product}`,{signal});
export const getGroupingPlacementQuote=(id:string,run:string,placement:string,product_ids:number[],request_key:string,signal?:AbortSignal)=>
    request<GroupingPlacementQuote>(`${placementPath(id,run)}/${placement}/quote`,{
        method:'POST',body:JSON.stringify({product_ids,request_key}),signal},true);
export const queueGroupingPlacement=(id:string,run:string,body:{
    request_key:string;method:GroupingPlacementMethod;source_id?:string;source_hash?:string;source_version?:string;confirm_save?:boolean;
    product_ids?:number[];confirm_paid?:boolean;quote?:GroupingPlacementQuote['quote'];quote_token?:string;
},signal?:AbortSignal)=>request<{id:string;status:GroupingStatus}>(placementPath(id,run),{method:'POST',body:JSON.stringify(body),signal});
interface GroupingComparisonResponse {experiment_id?:string;products?:ApiGroupingProduct[];items:{tile_size:number;response:{
    groups:{local_group_key:string;name:string;definition:string;members:{product_id:number}[]}[];unassigned:{product_id:number;reason:string}[]}}[]}
export const getGroupingComparison=async(id:string,experimentId:string,signal?:AbortSignal)=>{
    const result=await request<GroupingComparisonResponse>(`${GROUPING_PATH}/jobs/${id}/comparison`,{signal});
    if(Array.isArray(result.products))return {...result,experiment_id:result.experiment_id||experimentId,products:result.products};
    // Older servers return only saved responses. Resolve their frozen inputs through
    // the existing read-only, paginated products API; never submit a new job.
    const wanted=new Set(result.items.flatMap(item=>[
        ...item.response.groups.flatMap(group=>group.members.map(member=>member.product_id)),
        ...item.response.unassigned.map(member=>member.product_id)
    ]));
    const products:ApiGroupingProduct[]=[];
    let page=1,pages=1;
    while(page<=pages&&wanted.size){
        const batch=await getGroupingProducts(experimentId,page,signal);
        for(const product of batch.items){
            if(wanted.delete(product.product_id))products.push(product);
        }
        pages=batch.pages;page++;
    }
    return {...result,experiment_id:experimentId,products};
};
export const getUnavailablePurgeSchedules = (excludeId?:number) =>
    request<string[]>(`/purge-schedules/unavailable?${new URLSearchParams(excludeId ? {exclude_id:String(excludeId)} : {})}`, undefined, true);


export interface ApiAiConfig {model:string;quality?:string;reasoning_effort?:string;}
export interface ApiAiProfile {key:string;label:string;kind:'image'|'text';config:ApiAiConfig;default:ApiAiConfig;models:Array<{id:string;levels:string[];disabled_reason:string}>;}
export interface ApiAiSettings {items:ApiAiProfile[];}
export const getAiSettings=(signal?:AbortSignal)=>request<ApiAiSettings>('/ai-settings',{signal});
export const updateAiSettings=(key:string,config:ApiAiConfig)=>request<{key:string;config:ApiAiConfig}>(`/ai-settings/${encodeURIComponent(key)}`,{method:'PATCH',body:JSON.stringify(config)});

export const getLiveBrandOptions=(id:number,q:string,signal?:AbortSignal)=>request<{id:number;name:string}[]>(`/livestreams/${id}/brand-options?q=${encodeURIComponent(q)}`,{signal});

export const saveSourceOrder=(kind:'live'|'purchase',id:number,revision:number,changes:Record<string,string>)=>
    request<{order_revision:number;view_settings:Record<string,string>}>(`/${kind==='live'?'livestreams':'procurements'}/${id}/item-order`,{method:'PATCH',body:JSON.stringify({revision,...changes})});


export type ApiPrintTaskProduct={product_master_id:number;sku:string;item_id:number|null;image_url:string|null};
export type PrintTaskProductQuery={skus:string[];product_ids:number[];item_ids:number[]};
export const getPrintTaskProducts=(query:PrintTaskProductQuery,signal?:AbortSignal)=>
    request<{items:ApiPrintTaskProduct[]}>('/print-task-products/batch',{method:'POST',body:JSON.stringify(query),signal});

export const updatePoolCurrentCost=(id:number,price:number|null,currency:string,key:string)=>request<Pick<import('./supplier').SupplierProduct,'id'|'supply_price'|'price_currency'|'missing_purchase_fields'>>(`/supplier-products/${id}/cost`,{method:'PATCH',headers:productSaveHeaders(key),body:JSON.stringify({price,currency})});
